import { FormEvent, KeyboardEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  autoCrawlSite,
  createSavedAnalysis,
  createWatchlist,
  discoverClassify,
  discoverHashtags,
  discoverKeywords,
  discoverSaas,
  errorMessage,
  fetchMarketNearby,
  fetchMarketOpportunities,
  fetchPages,
  fetchSavedAnalyses,
  fetchSavedAnalysis,
  fetchSite,
  searchLive,
  suggestMarketAddresses,
  type AddressSuggestion,
  type AutoSiteResult,
  type BusinessReportCompetitor,
  type CompetitorClassification,
  type DiscoverKeywordGroup,
  type HashtagSignal,
  type ImportableTrendDomain,
  type ImportableTrendKind,
  type MarketCompetitor,
  type MarketMap,
  type MarketOpportunityResult,
  type OpportunityKind,
  type PageView,
  type SaasCompetitor,
  type SaasDiscoveryResult,
  type SavedAnalysis,
} from './api';
import { categoryLabel } from './categories';
import { estimateSpend, formatSpend } from './spesa';
import { MetricBars, ScoreArc, ZoneVisual } from './MarketVisuals';
import { localBusinessDetailUrl } from './LocalBusinessDetail';
import { CategoryIcon } from './InsightVisuals';

type AnalysisMode = 'local' | 'online';

interface FailedSearch {
  term: string;
  language: string;
  countries: string[];
  source: 'keyword' | 'social' | 'seed';
}

export interface AnalysisSession {
  kind?: 'competitor' | 'opportunity';
  mode: AnalysisMode;
  description: string;
  zone: string;
  businessType: string;
  countries: string[];
  siteUrl?: string;
  knownCompetitors?: string[];
  knownCompetitorsNotFound?: string[];
  keywords: string[];
  keywordGroups: DiscoverKeywordGroup[];
  hashtags: HashtagSignal[];
  tiktokUsed: boolean;
  socialWarning: string | null;
  failedTerms: FailedSearch[];
  classifications: CompetitorClassification[];
  saas?: SaasDiscoveryResult | null;
  market: MarketMap | null;
  opportunity?: MarketOpportunityResult;
  localAdsUnavailable: boolean;
  completedAt: string;
}

interface ProgressState {
  step: number;
  detail: string;
}

type SaasProgress = 'idle' | 'running' | 'done' | 'partial';

interface UnifiedCompetitor {
  key: string;
  name: string;
  label: 'diretto' | 'adiacente' | 'irrilevante';
  meta?: CompetitorClassification;
  saas?: SaasCompetitor;
}

interface MarketOption {
  code: string;
  label: string;
}

interface LocalRadiusOption {
  value: number;
  label: string;
}

const CACHE_KEY = ['competitor-analysis-session'] as const;

type AnalysisChoiceIconName = 'competitors' | 'opportunity' | 'local' | 'online';

function AnalysisChoiceIcon({ name }: { name: AnalysisChoiceIconName }) {
  const paths: Record<AnalysisChoiceIconName, ReactNode> = {
    competitors: <><circle cx="9" cy="9" r="3"/><path d="M3.5 19c.6-3.1 2.4-4.7 5.5-4.7s4.9 1.6 5.5 4.7M15 7.5a3 3 0 0 1 0 5.8M16.5 15c2.1.4 3.4 1.7 4 4"/></>,
    opportunity: <><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="2"/><path d="M12 2v3m0 14v3M2 12h3m14 0h3"/></>,
    local: <><path d="M4 11 12 4l8 7"/><path d="M6.5 10.5V20h11v-9.5M10 20v-6h4v6"/></>,
    online: <><circle cx="12" cy="12" r="8"/><path d="M4 12h16M12 4c2.2 2.3 3.2 5 3.2 8S14.2 17.7 12 20c-2.2-2.3-3.2-5-3.2-8S9.8 6.3 12 4Z"/></>,
  };
  return <span className="analysis-choice-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg></span>;
}
const numFmt = new Intl.NumberFormat('it-IT');
const compactFmt = new Intl.NumberFormat('it-IT', { notation: 'compact', maximumFractionDigits: 1 });
const distanceFmt = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 });
const densityFmt = new Intl.NumberFormat('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const rentFmt = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 });
const sourceDateFmt = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
const analysisDateFmt = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const LOCAL_RADII: LocalRadiusOption[] = [
  { value: 500, label: '500 m' },
  { value: 1_000, label: '1 km' },
  { value: 2_000, label: '2 km' },
  { value: 5_000, label: '5 km' },
];
const OPPORTUNITY_KIND_LABELS: Record<OpportunityKind, string> = {
  gap: 'Mancante',
  quality: 'Offerta debole',
  fragmentation: 'Nessun leader',
};
const IMPORTABLE_TREND_KIND_LABELS: Record<ImportableTrendKind, string> = {
  diffuso: 'Già in Italia',
  frontiera: 'Frontiera',
};
const IMPORTABLE_TREND_DOMAIN_LABELS: Record<ImportableTrendDomain, string> = {
  food: 'Food',
  'tempo libero': 'Tempo libero',
  servizi: 'Servizi',
  retail: 'Retail',
};
const MAIN_MARKETS: MarketOption[] = [
  { code: 'IT', label: 'Italia' },
  { code: 'DE', label: 'Germania' },
  { code: 'FR', label: 'Francia' },
  { code: 'ES', label: 'Spagna' },
  { code: 'GB', label: 'Regno Unito' },
  { code: 'NL', label: 'Paesi Bassi' },
];
const OTHER_MARKETS: MarketOption[] = [
  { code: 'AT', label: 'Austria' },
  { code: 'CH', label: 'Svizzera' },
  { code: 'BE', label: 'Belgio' },
  { code: 'IE', label: 'Irlanda' },
  { code: 'PT', label: 'Portogallo' },
  { code: 'PL', label: 'Polonia' },
  { code: 'SE', label: 'Svezia' },
  { code: 'DK', label: 'Danimarca' },
  { code: 'FI', label: 'Finlandia' },
  { code: 'GR', label: 'Grecia' },
  { code: 'CZ', label: 'Cechia' },
  { code: 'RO', label: 'Romania' },
  { code: 'HU', label: 'Ungheria' },
  { code: 'HR', label: 'Croazia' },
];
const MARKET_NAMES = new Map([...MAIN_MARKETS, ...OTHER_MARKETS].map((market) => [market.code, market.label]));
type AutoSiteViewState = AutoSiteResult | { status: 'loading'; snapshot: null; message: null };
interface QueuedAutoSite {
  run: () => Promise<AutoSiteResult>;
  resolve: (result: AutoSiteResult) => void;
  reject: (reason: unknown) => void;
}
const autoSiteTasks = new Map<string, Promise<AutoSiteResult>>();
const autoSiteQueue: QueuedAutoSite[] = [];
let activeAutoSites = 0;

function drainAutoSiteQueue() {
  while (activeAutoSites < 2 && autoSiteQueue.length > 0) {
    const next = autoSiteQueue.shift();
    if (!next) return;
    activeAutoSites += 1;
    next.run().then(next.resolve, next.reject).finally(() => {
      activeAutoSites -= 1;
      drainAutoSiteQueue();
    });
  }
}

function scheduleAutoSite(runId: string, pageId: string): Promise<AutoSiteResult> {
  const taskKey = runId + ':' + pageId;
  const existing = autoSiteTasks.get(taskKey);
  if (existing) return existing;
  const task = new Promise<AutoSiteResult>((resolve, reject) => {
    autoSiteQueue.push({ run: () => autoCrawlSite(pageId), resolve, reject });
    drainAutoSiteQueue();
  });
  autoSiteTasks.set(taskKey, task);
  return task;
}

function uniqueTerms(terms: string[]): string[] {
  const seen = new Set<string>();
  return terms.map((term) => term.trim()).filter((term) => {
    const key = term.toLocaleLowerCase('it');
    if (!term || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function competitorSearchTerm(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes(' ')) return trimmed;
  const withoutProtocol = trimmed.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  const host = withoutProtocol.split(/[/?#]/)[0];
  if (!host.includes('.')) return host;
  const parts = host.split('.').filter(Boolean);
  if (parts.length < 2) return host;
  const secondLevelSuffixes = new Set(['co', 'com', 'org', 'net']);
  const index = parts.length > 2 && secondLevelSuffixes.has(parts[parts.length - 2] ?? '') ? parts.length - 3 : parts.length - 2;
  return parts[Math.max(0, index)];
}

function competitorMatchesPage(seed: string, pageName: string | null): boolean {
  if (!pageName) return false;
  const searchValue = competitorSearchTerm(seed).toLocaleLowerCase('it');
  const needle = searchValue.replace(/[^a-z0-9]/g, '');
  const candidate = pageName.toLocaleLowerCase('it').replace(/[^a-z0-9]/g, '');
  const distinctiveToken = searchValue.split(/[^a-z0-9]+/).filter((token) => token.length >= 3).sort((a, b) => b.length - a.length)[0];
  return needle.length >= 3 && (candidate.includes(needle) || needle.includes(candidate) || Boolean(distinctiveToken && candidate.includes(distinctiveToken)));
}

function normalizedCompetitorName(value: string): string {
  return value
    .toLocaleLowerCase('it')
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\.(com|io|ai|co|net|org|app)(?=\b|\/|$)/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function competitorLabelRank(label: UnifiedCompetitor['label']): number {
  return label === 'diretto' ? 0 : label === 'adiacente' ? 1 : 2;
}

function strongestLabel(meta?: CompetitorClassification, saas?: SaasCompetitor): UnifiedCompetitor['label'] {
  const labels = [meta?.label, saas?.label].filter(Boolean) as UnifiedCompetitor['label'][];
  return labels.sort((a, b) => competitorLabelRank(a) - competitorLabelRank(b))[0] ?? 'irrilevante';
}

function saasStableKey(row: SaasCompetitor): string {
  return row.slug?.trim() || normalizedCompetitorName(row.name);
}

function compareSaasStrength(a: SaasCompetitor, b: SaasCompetitor): number {
  return competitorLabelRank(a.label) - competitorLabelRank(b.label)
    || (b.reviews ?? 0) - (a.reviews ?? 0);
}

function mergeCompetitors(metaRows: CompetitorClassification[], saasRows: SaasCompetitor[]): UnifiedCompetitor[] {
  const remainingSaas = new Map<string, SaasCompetitor>();
  saasRows.forEach((row) => {
    const key = saasStableKey(row);
    if (!key) return;
    const current = remainingSaas.get(key);
    if (!current || compareSaasStrength(row, current) < 0) remainingSaas.set(key, row);
  });

  const merged: UnifiedCompetitor[] = metaRows.map((meta) => {
    const name = meta.pageName ?? meta.pageId;
    const matchKey = meta.pageName ? normalizedCompetitorName(meta.pageName) : '';
    const match = matchKey ? [...remainingSaas.entries()]
      .filter(([, row]) => normalizedCompetitorName(row.name) === matchKey || normalizedCompetitorName(row.vendor ?? '') === matchKey)
      .sort((a, b) => {
        const aNameMatch = normalizedCompetitorName(a[1].name) === matchKey ? 0 : 1;
        const bNameMatch = normalizedCompetitorName(b[1].name) === matchKey ? 0 : 1;
        return aNameMatch - bNameMatch || compareSaasStrength(a[1], b[1]);
      })[0] : undefined;
    const saas = match?.[1];
    if (match) remainingSaas.delete(match[0]);
    return {
      key: 'meta:' + meta.pageId,
      name: saas?.name || name,
      label: strongestLabel(meta, saas),
      meta,
      saas,
    } satisfies UnifiedCompetitor;
  });

  remainingSaas.forEach((saas, key) => merged.push({
    key: 'g2:' + key,
    name: saas.name,
    label: saas.label,
    saas,
  }));

  return merged.sort((a, b) => {
    const labelDelta = competitorLabelRank(a.label) - competitorLabelRank(b.label);
    if (labelDelta !== 0) return labelDelta;
    const sourceRank = (row: UnifiedCompetitor) => row.meta && row.saas ? 0 : row.meta ? 1 : 2;
    const sourceDelta = sourceRank(a) - sourceRank(b);
    if (sourceDelta !== 0) return sourceDelta;
    if (a.meta || b.meta) {
      const reachDelta = (b.meta?.totalReach ?? 0) - (a.meta?.totalReach ?? 0);
      if (reachDelta !== 0) return reachDelta;
    }
    return (b.saas?.reviews ?? 0) - (a.saas?.reviews ?? 0);
  });
}

function marketName(code: string): string {
  return MARKET_NAMES.get(code) ?? code;
}

function rowCountries(row: CompetitorClassification, analyzed: string[]): string[] {
  const present = row.countries ?? [];
  return analyzed.filter((country) => present.includes(country));
}

function rankByMarketStrength(rows: CompetitorClassification[], analyzed: string[]): CompetitorClassification[] {
  return [...rows].sort((a, b) => rowCountries(b, analyzed).length - rowCountries(a, analyzed).length || b.totalReach - a.totalReach);
}

function opportunityScore(item: MarketCompetitor, average: number): number {
  if (item.strongPresence || item.rating == null || item.rating < average) return 0;
  return item.rating * 1000 - (item.reviewsCount ?? 0);
}

function formatDistance(meters: number): string {
  return meters < 1_000
    ? numFmt.format(meters) + ' m'
    : distanceFmt.format(meters / 1_000) + ' km';
}

function formatSourceDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : sourceDateFmt.format(date);
}

function failedSearchSummary(rows: FailedSearch[]): string {
  const groups = new Map<string, string[]>();
  rows.forEach((row) => {
    const label = row.source === 'social'
      ? 'linguaggio social (' + row.countries.join(', ') + ')'
      : row.source === 'seed'
        ? 'competitor indicati (' + row.countries.join(', ') + ')'
        : row.language + ' (' + row.countries.join(', ') + ')';
    groups.set(label, [...(groups.get(label) ?? []), row.term]);
  });
  return [...groups.entries()].map(([label, terms]) => label + ': ' + terms.join(', ')).join(' · ');
}

function analysisTitle(session: AnalysisSession): string {
  if (session.opportunity) return `Opportunità · ${session.zone}`;
  return (session.businessType || session.description).trim();
}

function analysisSummary(session: AnalysisSession): string {
  if (session.opportunity) {
    const count = session.opportunity.opportunities.length;
    const trends = session.opportunity.importableTrends?.length ?? 0;
    const registered = session.opportunity.registry?.totalCompanies;
    return `${count} opportunità${trends > 0 ? ` · ${trends} trend da importare` : ''} · ${session.opportunity.businessesFound} attività Google${registered != null ? ` · ${registered} aziende registrate` : ''} · raggio ${formatDistance(session.opportunity.radiusMeters)}`;
  }
  if (session.mode === 'local') {
    const relevantAds = session.classifications.filter((row) => row.label !== 'irrilevante').length;
    const activities = session.market?.count ?? 0;
    return `${activities} ${activities === 1 ? 'attività locale' : 'attività locali'} · ${relevantAds} inserzionisti rilevanti`;
  }
  const unified = mergeCompetitors(session.classifications, session.saas?.competitors ?? []);
  const direct = unified.filter((row) => row.label === 'diretto').length;
  const adjacent = unified.filter((row) => row.label === 'adiacente').length;
  const g2 = session.saas?.competitors.length ?? 0;
  return `${direct} diretti · ${adjacent} adiacenti${g2 > 0 ? ` · ${g2} da G2` : ''}`;
}

function RestoreAnalysisView() {
  return <div className="analysis-restore-loading" role="status"><p className="eyebrow">Analisi competitor</p><h1>Riapro l’ultima analisi</h1><p>Recupero il panorama salvato e tutti i suoi dati.</p><div aria-hidden="true"><span/><span/><span/></div></div>;
}

function OpportunityProgressView({ progress }: { progress: ProgressState }) {
  const steps = ['Risolvo l’indirizzo', 'Censisco le categorie', 'Confronto densità e benchmark', 'Confronto con le capitali europee…', 'Preparo le opportunità'];
  return <section className="analysis-progress opportunity-progress" aria-labelledby="opportunity-progress-title" aria-live="polite">
    <p className="eyebrow">Opportunità di zona</p>
    <h1 id="opportunity-progress-title">Sto leggendo il tessuto commerciale</h1>
    <p>Può richiedere fino a 2 minuti. Censisco le attività nel raggio e confronto i dati misurabili con le medie nazionali.</p>
    <ol>{steps.map((label, index) => {
      const position = index + 1;
      const state = position < progress.step ? 'is-done' : position === progress.step ? 'is-current' : '';
      return <li className={state} key={label} aria-current={position === progress.step ? 'step' : undefined}><span>{position < progress.step ? '✓' : position}</span><div><strong>{label}</strong>{position === progress.step && <small>{progress.detail}</small>}</div></li>;
    })}</ol>
    <p className="opportunity-progress-source">Google Maps, Registro Imprese, OMI, ISTAT/WorldPop, benchmark nazionali e capitali europee. Nessun consumo di credito Apify.</p>
  </section>;
}

interface OpportunityResultsViewProps {
  session: AnalysisSession;
  restoredAnalysis: SavedAnalysis<AnalysisSession> | null;
  saveState: 'idle' | 'pending' | 'success' | 'error';
  onReset: () => void;
  onRetrySave: () => void;
}

function OpportunityResultsView({ session, restoredAnalysis, saveState, onReset, onRetrySave }: OpportunityResultsViewProps) {
  const result = session.opportunity!;
  const opportunities = [...result.opportunities].sort((a, b) => {
    const kindOrder = Number(b.kind === 'gap') - Number(a.kind === 'gap');
    return kindOrder || b.score - a.score;
  });
  const importableTrends = [...(result.importableTrends ?? [])].sort((a, b) => {
    const kindOrder = Number(b.kind === 'diffuso') - Number(a.kind === 'diffuso');
    return kindOrder || b.score - a.score;
  });
  const creativeIdeas = result.creativeIdeas ?? [];
  const rentQuote = result.rentQuote ?? null;
  const registry = result.registry ?? null;
  const registryCategories = registry?.byCategory ?? [];
  const strongestSignal = opportunities[0] ? Math.round(Math.max(0, Math.min(1, opportunities[0].score)) * 100) : 0;
  const potentialLabel = strongestSignal >= 75 ? 'Alto' : strongestSignal >= 50 ? 'Medio' : 'Basso';
  const topSignals = opportunities.slice(0, 4);
  const googleCensusByKey = new Map(result.census.map((row) => [row.key, row]));
  const rentZoneLabel = rentQuote
    ? [rentQuote.zoneName, rentQuote.fascia ? `(${rentQuote.fascia})` : null].filter(Boolean).join(' ')
    : '';
  const saveLabel = restoredAnalysis
    ? `Salvata il ${analysisDateFmt.format(new Date(restoredAnalysis.createdAt))}`
    : saveState === 'pending' ? 'Salvataggio automatico…'
      : saveState === 'success' ? 'Salvata automaticamente'
        : saveState === 'error' ? 'Salvataggio non riuscito' : 'Analisi completata ora';

  return <div className="analysis-panorama opportunity-results">
    {restoredAnalysis && <div className="analysis-restored-note"><span>Analisi del <time dateTime={restoredAnalysis.createdAt}>{analysisDateFmt.format(new Date(restoredAnalysis.createdAt))}</time></span><button type="button" className="btn-quiet" onClick={onReset}>Nuova analisi</button></div>}
    <header className="analysis-panorama-head"><div><p className="eyebrow">Opportunità di zona</p><h1 id="analysis-result-title" tabIndex={-1}>Cosa può avere spazio qui</h1><p>{session.zone} · raggio {formatDistance(result.radiusMeters)}</p></div>{!restoredAnalysis && <div><button type="button" className="btn-secondary" onClick={onReset}>Nuova analisi</button></div>}</header>

    <section className="opportunity-visual-summary" aria-label="Sintesi visuale della zona">
      <ZoneVisual radiusLabel={formatDistance(result.radiusMeters)} markers={Math.max(3, Math.min(7, opportunities.length))} title={session.zone} />
      <div className="opportunity-score-panel">
        <div className="opportunity-potential-head"><ScoreArc value={strongestSignal} label="Potenziale di zona" /><div className="opportunity-score-copy"><p className="eyebrow">Potenziale di zona</p><h2>{potentialLabel}</h2><p>{potentialLabel === 'Alto' ? 'La zona mostra opportunità da approfondire.' : potentialLabel === 'Medio' ? 'Ci sono segnali utili, ma non ancora dominanti.' : 'I segnali disponibili sono ancora deboli.'}</p></div></div>
        {topSignals.length > 0 && <ol className="opportunity-top-signals">{topSignals.map((item, index) => {
          const score = Math.round(Math.max(0, Math.min(1, item.score)) * 100);
          return <li key={`${item.kind}-${item.key}`}><CategoryIcon category={`${item.key} ${item.label}`}/><div><span>{item.label}</span><progress max="100" value={score} aria-label={`${item.label}: ${score} su 100`}>{score}%</progress></div><strong>#{index + 1}</strong></li>;
        })}</ol>}
      </div>
    </section>

    <dl className="opportunity-scope" aria-label="Area censita">
      <div><dt>Indirizzo</dt><dd>{session.zone}</dd></div>
      <div><dt>Raggio</dt><dd>{formatDistance(result.radiusMeters)}</dd></div>
      <div><dt>Abitanti</dt><dd>{result.population != null ? <>~{numFmt.format(result.population)} <small className="access-label estimated">Stima</small></> : 'n/d'}</dd></div>
      <div><dt>Attività Google</dt><dd>{numFmt.format(result.businessesFound)}</dd></div>
    </dl>
    {(rentQuote || registry) && <div className="opportunity-context-cards">
      {rentQuote && <section className="opportunity-rent" aria-labelledby="opportunity-rent-title">
        <div><p className="eyebrow">Costo di ingresso</p><h2 id="opportunity-rent-title">Negozi in affitto</h2><p className="opportunity-rent-prices"><strong>€{rentFmt.format(rentQuote.minSqm)}–{rentFmt.format(rentQuote.maxSqm)}/m² al mese</strong><span>Media €{rentFmt.format(rentQuote.medSqm)}</span></p><small>Quotazioni OMI {rentQuote.year} S{rentQuote.semester}{rentZoneLabel && <> · zona {rentZoneLabel}</>}</small></div>
        <div className="opportunity-rent-example"><span>Esempio per 60 m²</span><strong>Circa €{numFmt.format(rentQuote.monthlyFor60sqm)}</strong><small>al mese</small></div>
      </section>}
      {registry && <section className="opportunity-registry" aria-labelledby="opportunity-registry-title">
        <header><span className="opportunity-registry-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21h16M6 21V9h12v12M4 9l8-6 8 6M9 13h2m2 0h2m-6 4h2m2 0h2"/></svg></span><div><p className="eyebrow">Dal Registro Imprese</p><h2 id="opportunity-registry-title">Tessuto ufficiale della zona</h2></div></header>
        <dl><div><dt>Aziende registrate</dt><dd>{numFmt.format(registry.totalCompanies)}</dd></div><div><dt>Nuove negli ultimi 90 giorni</dt><dd>{numFmt.format(registry.newLast90d)}</dd></div></dl>
        <details className="opportunity-registry-detail">
          <summary><span><strong>Confronta per categoria</strong><small>Registro ufficiale e presenza rilevata da Google</small></span><span>{registryCategories.length}</span></summary>
          <div role="region" aria-label="Confronto Registro Imprese e Google per categoria" tabIndex={0}><table><thead><tr><th>Categoria</th><th>Registro</th><th>Google</th><th>Scarto</th></tr></thead><tbody>{registryCategories.map((category) => {
            const googleCount = googleCensusByKey.get(category.key)?.count;
            const gap = googleCount == null ? null : category.count - googleCount;
            return <tr key={category.key}><th scope="row">{category.label}</th><td>{numFmt.format(category.count)}</td><td>{googleCount == null ? 'n/d' : numFmt.format(googleCount)}</td><td className={gap != null && gap > 0 ? 'is-gap' : undefined}>{gap == null ? 'n/d' : gap > 0 ? <><strong>+{numFmt.format(gap)}</strong><small>possibile gap digitale</small></> : '—'}</td></tr>;
          })}</tbody></table></div>
        </details>
        <footer><strong>Fonte: Registro Imprese</strong><span>I conteggi usano la sede legale, che può differire dalla sede operativa. Il confronto con Google è indicativo.</span></footer>
      </section>}
    </div>}
    <p className="opportunity-save-state" role="status">{saveLabel}</p>
    {saveState === 'error' && <div className="notice notice-warning"><p>Il risultato è pronto, ma non è stato salvato nello storico.</p><button type="button" className="btn-secondary" onClick={onRetrySave}>Riprova il salvataggio</button></div>}

    <section className="opportunity-section" aria-labelledby="opportunities-title">
      <div className="section-heading"><div><h2 id="opportunities-title">Le opportunità</h2><p>Ordinate per forza del segnale; i gap di densità hanno precedenza.</p></div><span>{opportunities.length}</span></div>
      {opportunities.length > 0 ? <ol className="opportunity-list">{opportunities.map((opportunity) => {
        const score = Math.round(Math.max(0, Math.min(1, opportunity.score)) * 100);
        return <li className={`opportunity-item is-${opportunity.kind}`} key={`${opportunity.kind}-${opportunity.key}`}>
          <header><div><span className={`opportunity-kind is-${opportunity.kind}`}>{OPPORTUNITY_KIND_LABELS[opportunity.kind]}</span><h3>{opportunity.label}</h3></div><strong>{score}%</strong></header>
          <ul>{opportunity.evidence.map((evidence) => <li key={evidence}>{evidence}</li>)}</ul>
          <div className="opportunity-score"><span>Forza del segnale</span><progress className={`is-${opportunity.kind}`} max="100" value={score} aria-label={`Forza del segnale ${score}%`}>{score}%</progress></div>
        </li>;
      })}</ol> : <div className="activity-empty opportunity-empty"><strong>Nessun gap netto nei dati disponibili</strong><p>Il censimento non evidenzia categorie chiaramente sottoservite. Apri il dettaglio completo per confrontare densità e benchmark.</p></div>}
    </section>

    {importableTrends.length > 0 && <section className="importable-trends" aria-labelledby="importable-trends-title">
      <div className="section-heading"><div><p className="eyebrow">Confronto europeo</p><h2 id="importable-trends-title">Trend da importare</h2><p>Format poco presenti nella zona ma già visibili nelle principali capitali europee.</p></div><span>{importableTrends.length}</span></div>
      <ol className="importable-trend-list">{importableTrends.map((trend) => {
        const score = Math.round(Math.max(0, Math.min(1, trend.score)) * 100);
        const comparisonMax = Math.max(trend.localCount, trend.hubAvg, trend.milanCount ?? 0, 1);
        return <li className={`importable-trend-item is-${trend.kind}`} key={trend.key}>
          <header>
            <div><div className="importable-trend-tags"><span className={`importable-trend-kind is-${trend.kind}`}>{IMPORTABLE_TREND_KIND_LABELS[trend.kind]}</span>{trend.domain && <span className="importable-trend-domain">{IMPORTABLE_TREND_DOMAIN_LABELS[trend.domain]}</span>}</div><h3>{trend.label}</h3></div>
            <div className="importable-trend-strength"><strong>{score}%</strong><small>forza</small></div>
          </header>
          <ul className="importable-trend-evidence">{trend.evidence.map((evidence) => <li key={evidence}>{evidence}</li>)}</ul>
          <dl className="importable-trend-comparison" aria-label={`Confronto presenze per ${trend.label}`}>
            <div><dt>Zona</dt><dd><progress className="is-local" max={comparisonMax} value={trend.localCount} aria-label={`Zona: ${trend.localCount}`}>{trend.localCount}</progress><strong>{numFmt.format(trend.localCount)}</strong></dd></div>
            <div><dt>Media EU</dt><dd><progress className="is-eu" max={comparisonMax} value={trend.hubAvg} aria-label={`Media capitali europee: ${densityFmt.format(trend.hubAvg)}`}>{trend.hubAvg}</progress><strong>{densityFmt.format(trend.hubAvg)}</strong></dd></div>
            <div><dt>Milano</dt><dd><progress className="is-milan" max={comparisonMax} value={trend.milanCount ?? 0} aria-label={trend.milanCount != null ? `Milano: ${trend.milanCount}` : 'Milano: dato non disponibile'}>{trend.milanCount ?? 0}</progress><strong>{trend.milanCount != null ? numFmt.format(trend.milanCount) : 'n/d'}</strong></dd></div>
          </dl>
          <p className="importable-trend-hub"><span>Presenza più forte</span><strong>{trend.topHub}</strong></p>
        </li>;
      })}</ol>
    </section>}

    {creativeIdeas.length > 0 && <section className="creative-opportunities" aria-labelledby="creative-opportunities-title">
      <div className="section-heading"><div><p className="eyebrow">Brainstorm ancorato ai dati</p><h2 id="creative-opportunities-title">Idee da esplorare</h2><p>Combinazioni meno ovvie costruite sui segnali specifici della zona.</p></div><span>{creativeIdeas.length}</span></div>
      <ol className="creative-opportunity-list">{creativeIdeas.map((idea, index) => <li className="creative-opportunity-item" key={`${idea.idea}-${index}`}>
        <header><span className="creative-opportunity-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6M10 22h4"/><path d="M8.2 14.8A7 7 0 1 1 15.8 14.8c-.8.7-1.3 1.5-1.4 2.2H9.6c-.1-.7-.6-1.5-1.4-2.2Z"/></svg></span><div><small>Ipotesi {index + 1}</small><h3>{idea.idea}</h3></div></header>
        <p>{idea.rationale}</p>
        {idea.basedOn.length > 0 && <ul aria-label={`Dati alla base di ${idea.idea}`}>{idea.basedOn.map((datum) => <li key={datum}>dato: {datum}</li>)}</ul>}
      </li>)}</ol>
      <p className="creative-opportunity-disclaimer">Ipotesi generate dall’AI incrociando i dati della zona: spunti di ragionamento, non raccomandazioni di investimento.</p>
    </section>}

    {result.reading && <section className="opportunity-reading" aria-labelledby="opportunity-reading-title"><p className="eyebrow">Lettura prudente</p><h2 id="opportunity-reading-title">Come interpretare i segnali</h2><p>{result.reading}</p></section>}

    <details className="opportunity-census">
      <summary><span><strong>Censimento completo</strong><small>Densità locale confrontata con la media italiana</small></span><span>{result.census.length}</span></summary>
      <div role="region" aria-label="Tabella censimento attività" tabIndex={0}><table><thead><tr><th>Categoria</th><th>Attività</th><th>Rating</th><th>Densità / 10.000</th><th>Media Italia</th></tr></thead><tbody>{result.census.map((row) => <tr key={row.key}><th scope="row">{row.label}</th><td>{numFmt.format(row.count)}{row.capped ? '+' : ''}</td><td>{row.avgRating != null ? <>★ {densityFmt.format(row.avgRating)}<small>{numFmt.format(row.totalReviews)} recensioni</small></> : 'n/d'}</td><td>{row.densityPer10k != null ? densityFmt.format(row.densityPer10k) : 'n/d'}</td><td>{densityFmt.format(row.benchmarkPer10k)}</td></tr>)}</tbody></table></div>
    </details>

    <p className="data-note opportunity-disclaimer">{result.disclaimer}</p>
  </div>;
}

function ProgressView({ mode, progress, hasSeeds, saasProgress }: { mode: AnalysisMode; progress: ProgressState; hasSeeds: boolean; saasProgress: SaasProgress }) {
  const steps = mode === 'local'
    ? ['Capisco attività e zona', 'Mappo il mercato locale', 'Confronto chi si promuove', 'Preparo il quadro']
    : [hasSeeds ? 'Leggo il tuo sito e i competitor indicati' : 'Capisco i mercati', 'Ascolto il linguaggio social', 'Cerco chi ci investe', 'Tolgo il rumore', 'Preparo il quadro'];

  return (
    <section className="analysis-progress" aria-labelledby="analysis-progress-title" aria-live="polite">
      <p className="eyebrow">Analisi in corso</p>
      <h1 id="analysis-progress-title">Sto costruendo il tuo panorama</h1>
      <p>{mode === 'online' ? 'L’ascolto social può richiedere alcuni minuti. Le prime ricerche partono nel frattempo.' : 'Può richiedere fino a 90 secondi. Puoi seguire ogni passaggio mentre Osservio confronta i segnali.'}</p>
      {mode === 'online' && saasProgress !== 'idle' && <div className={`analysis-g2-progress is-${saasProgress}`}><span aria-hidden="true">{saasProgress === 'done' ? '✓' : saasProgress === 'partial' ? '!' : 'G2'}</span><div><strong>{saasProgress === 'running' ? 'Cerco nel catalogo software (G2)…' : saasProgress === 'done' ? 'Catalogo software G2 completato' : 'Catalogo G2 disponibile in parte'}</strong><small><span className="source-cost-badge">Apify</span> Ricerca parallela ai dati pubblicitari Meta</small></div></div>}
      <ol>
        {steps.map((label, index) => {
          const position = index + 1;
          const state = position < progress.step ? 'is-done' : position === progress.step ? 'is-current' : '';
          return <li className={state} key={label} aria-current={position === progress.step ? 'step' : undefined}><span>{position < progress.step ? '✓' : position}</span><div><strong>{label}</strong>{position === progress.step && progress.detail && <small>{progress.detail}</small>}</div></li>;
        })}
      </ol>
    </section>
  );
}

interface AnalysisSeedsProps {
  siteUrl: string;
  knownCompetitors: string[];
  draft: string;
  onSiteUrlChange: (value: string) => void;
  onDraftChange: (value: string) => void;
  onDraftKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onAdd: () => void;
  onRemove: (value: string) => void;
}

function AnalysisSeeds({ siteUrl, knownCompetitors, draft, onSiteUrlChange, onDraftChange, onDraftKeyDown, onAdd, onRemove }: AnalysisSeedsProps) {
  const full = knownCompetitors.length >= 5;
  return <fieldset className="analysis-seeds">
    <legend>Informazioni già note <small>Opzionali</small></legend>
    <p>Aiutano Osservio a usare il vocabolario reale del tuo mercato.</p>
    <div className="analysis-seed-grid">
      <label className="field"><span>Il tuo sito</span><input type="text" inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={siteUrl} onChange={(event) => onSiteUrlChange(event.target.value)} placeholder="es. osservio.it" /><small className="analysis-field-hint">Leggo soltanto la homepage durante questa analisi.</small></label>
      <div className="analysis-known-field">
        <label htmlFor="known-competitor">Competitor che conosci <small>{knownCompetitors.length}/5</small></label>
        <div className="analysis-seed-entry"><input id="known-competitor" type="text" value={draft} disabled={full} onChange={(event) => onDraftChange(event.target.value)} onKeyDown={onDraftKeyDown} placeholder={full ? 'Limite raggiunto' : 'Nome o dominio'} aria-describedby="known-competitor-hint" /><button type="button" className="btn-secondary" disabled={full || !draft.trim()} onClick={onAdd}>Aggiungi</button></div>
        <small className="analysis-field-hint" id="known-competitor-hint">Premi Invio dopo ogni nome. Li cercherò direttamente anche su Meta.</small>
      </div>
    </div>
    {knownCompetitors.length > 0 && <ul className="analysis-known-chips" aria-label="Competitor indicati">{knownCompetitors.map((competitor) => <li key={competitor}><button type="button" onClick={() => onRemove(competitor)} aria-label={`Rimuovi ${competitor}`}><span>{competitor}</span><span aria-hidden="true">×</span></button></li>)}</ul>}
  </fieldset>;
}

function SiteSignal({ pageId, autoRunId }: { pageId: string; autoRunId?: string }) {
  const site = useQuery({
    queryKey: ['site', pageId],
    queryFn: () => fetchSite(pageId),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const autoSite = useQuery<AutoSiteViewState | null>({
    queryKey: ['site-auto', autoRunId ?? 'manual', pageId],
    queryFn: async () => null,
    enabled: false,
  });
  const snapshot = autoSite.data?.status === 'ok' && autoSite.data.snapshot ? autoSite.data.snapshot : site.data;
  const pixels = snapshot?.techPixels?.split(',').map((item) => item.trim()).filter(Boolean) ?? [];
  const hasSignals = Boolean(snapshot?.pricing || snapshot?.features || pixels.length > 0);
  const isAutoLoading = autoRunId != null && autoSite.data?.status === 'loading';

  if (isAutoLoading && !hasSignals) return <div className="analysis-site-loading" role="status"><span>Analizzo il sito…</span><i /><i /></div>;
  if (!snapshot || !hasSignals) return null;

  return <div className="analysis-site-signal"><span>Dal sito{isAutoLoading ? ' · aggiorno' : ''}</span>{snapshot.pricing && <p><strong>Prezzi:</strong> {snapshot.pricing}</p>}{snapshot.features && <p><strong>Offerta:</strong> {snapshot.features}</p>}{pixels.length > 0 && <div>{pixels.slice(0, 4).map((pixel) => <small key={pixel}>{pixel}</small>)}</div>}</div>;
}

function OnlineCompetitorRow({ row, page, markets, autoRunId }: { row: CompetitorClassification; page?: PageView; markets: string[]; autoRunId?: string }) {
  const name = row.pageName ?? row.pageId;
  const spend = formatSpend(estimateSpend(row.totalReach, page?.category ?? null));
  const presentMarkets = rowCountries(row, markets);
  return (
    <article className={presentMarkets.length > 1 ? 'analysis-competitor-row is-multimarket' : 'analysis-competitor-row'}>
      <header>
        <span className="analysis-avatar" aria-hidden="true">{name.trim().slice(0, 2).toUpperCase()}</span>
        <div><h3>{name}</h3><p>{row.reason || 'Corrispondenza con il mercato analizzato.'}</p></div>
        <div className="analysis-row-badges">
          {presentMarkets.length > 0 && <div className="competitor-country-badges" aria-label={'Presente in ' + presentMarkets.map(marketName).join(', ')}>{presentMarkets.map((country) => <span key={country}>{country}</span>)}</div>}
          {presentMarkets.length > 1 && <small>Player multi-mercato</small>}
          <span className={'match-badge ' + row.label}>{row.label === 'diretto' ? 'Diretto' : row.label === 'adiacente' ? 'Adiacente' : 'Escluso'}</span>
        </div>
      </header>
      <dl className="analysis-row-metrics">
        <div><dt>Annunci</dt><dd>{numFmt.format(row.adsCount)}</dd></div>
        <div><dt>Attivi</dt><dd>{page ? numFmt.format(page.activeCount) : 'n/d'}</dd></div>
        <div><dt>Reach</dt><dd>{row.totalReach > 0 ? compactFmt.format(row.totalReach) : 'n/d'}</dd></div>
        <div><dt>Spesa</dt><dd>{spend ? <>{spend} <small className="access-label estimated">Stimata</small></> : 'n/d'}</dd></div>
        {page?.rating != null && <div><dt>Google</dt><dd>★ {page.rating} <small>{numFmt.format(page.reviewsCount ?? 0)}</small></dd></div>}
      </dl>
      <SiteSignal pageId={row.pageId} autoRunId={autoRunId} />
      <div className="analysis-row-actions"><Link to={'/competitor/' + encodeURIComponent(row.pageId)} state={{ from: '/competitor', backLabel: 'Panorama' }}>Apri dettaglio</Link><Link to={'/annunci?pageId=' + encodeURIComponent(row.pageId) + '&pageName=' + encodeURIComponent(name)} state={{ from: '/competitor', backLabel: 'Panorama' }}>Vedi annunci</Link></div>
    </article>
  );
}

function OnlineGroup({ title, description, rows, pages, markets, autoSiteIds, autoRunId }: { title: string; description: string; rows: CompetitorClassification[]; pages: PageView[]; markets: string[]; autoSiteIds?: Set<string>; autoRunId?: string }) {
  if (rows.length === 0) return null;
  const pageById = new Map(pages.map((page) => [page.pageId, page]));
  return <section className="analysis-result-group"><div className="section-heading"><div><h2>{title}</h2><p>{description}</p></div><span>{rows.length}</span></div><div className="analysis-competitor-list">{rows.map((row) => <OnlineCompetitorRow key={row.pageId} row={row} page={pageById.get(row.pageId)} markets={markets} autoRunId={autoSiteIds?.has(row.pageId) ? autoRunId : undefined} />)}</div></section>;
}

function g2Url(url: string | null): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return 'https://www.g2.com' + (url.startsWith('/') ? url : '/' + url);
}

function G2Coverage({ result }: { result: SaasDiscoveryResult }) {
  if (result.categories.length === 0 && result.candidates === 0) return null;
  const categoryLabelText = result.categories.length === 1 ? 'categoria analizzata' : 'categorie analizzate';
  const candidateLabelText = result.candidates === 1 ? 'prodotto valutato' : 'prodotti valutati';
  return <div className="analysis-g2-coverage" role="note" aria-label="Copertura della ricerca G2"><span className="source-cost-badge">G2</span><p><strong>{result.categories.length} {categoryLabelText}</strong><span aria-hidden="true"> · </span>{result.candidates} {candidateLabelText}</p></div>;
}

function UnifiedOnlineCompetitorRow({ item, page, markets, autoRunId }: { item: UnifiedCompetitor; page?: PageView; markets: string[]; autoRunId?: string }) {
  const { meta, saas } = item;
  const presentMarkets = meta ? rowCountries(meta, markets) : [];
  const spend = meta ? formatSpend(estimateSpend(meta.totalReach, page?.category ?? null)) : null;
  const productUrl = g2Url(saas?.url ?? null);
  const description = saas ? saas.description : meta?.reason;
  const saasReason = saas ? saas.reason?.trim() || 'Classificato in base a nome e categoria G2' : null;
  const awards = saas?.awards ?? [];
  const sourceDate = formatSourceDate(saas?.scrapedAt ?? null);

  return <article className={meta && saas ? 'analysis-competitor-row is-merged' : saas ? 'analysis-competitor-row is-g2' : 'analysis-competitor-row'}>
    <header>
      <span className="analysis-avatar" aria-hidden="true">{item.name.trim().slice(0, 2).toUpperCase()}{saas?.logoUrl && <img src={saas.logoUrl} alt="" loading="lazy" referrerPolicy="no-referrer" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}</span>
      <div><h3>{item.name}</h3>{saas?.vendor && <span className="analysis-g2-vendor">di {saas.vendor}</span>}{description && <p>{description}</p>}{saasReason && <small className="analysis-unified-reason"><strong>Perché:</strong> {saasReason}</small>}</div>
      <div className="analysis-row-badges">
        <div className="analysis-source-badges" aria-label={`Fonti: ${[meta && 'Meta Ads', saas && 'G2'].filter(Boolean).join(', ')}`}>{meta && <span className="is-meta">Meta Ads</span>}{saas && <span className="is-g2">G2</span>}</div>
        {presentMarkets.length > 0 && <div className="competitor-country-badges" aria-label={'Presente in ' + presentMarkets.map(marketName).join(', ')}>{presentMarkets.map((country) => <span key={country}>{country}</span>)}</div>}
        <span className={'match-badge ' + item.label}>{item.label === 'diretto' ? 'Diretto' : item.label === 'adiacente' ? 'Adiacente' : 'Escluso'}</span>
      </div>
    </header>
    {meta && <dl className="analysis-row-metrics" aria-label="Segnali Meta Ads">
      <div><dt>Annunci</dt><dd>{numFmt.format(meta.adsCount)}</dd></div>
      <div><dt>Attivi</dt><dd>{page ? numFmt.format(page.activeCount) : 'n/d'}</dd></div>
      <div><dt>Reach</dt><dd>{meta.totalReach > 0 ? compactFmt.format(meta.totalReach) : 'n/d'}</dd></div>
      <div><dt>Spesa</dt><dd>{spend ? <>{spend} <small className="access-label estimated">Stimata</small></> : 'n/d'}</dd></div>
      {page?.rating != null && <div><dt>Google</dt><dd>★ {page.rating} <small>{numFmt.format(page.reviewsCount ?? 0)}</small></dd></div>}
    </dl>}
    {saas && <section className="analysis-g2-signal" aria-label="Segnali catalogo G2"><div className="analysis-g2-signal-head"><span>Catalogo G2</span><div>{saas.position != null && <small>#{saas.position} in categoria</small>}{saas.category && <small>{saas.category}</small>}</div></div><dl><div><dt>Rating</dt><dd>{saas.rating != null ? `★ ${saas.rating}/5` : 'n/d'}</dd></div><div><dt>Recensioni</dt><dd>{saas.reviews != null ? numFmt.format(saas.reviews) : 'n/d'}</dd></div><div><dt>Da</dt><dd>{saas.entryPrice ?? 'n/d'}</dd></div></dl>{(saas.sponsored || awards.length > 0) && <div className="analysis-g2-flags">{saas.sponsored && <span className="is-sponsored">Sponsorizzato</span>}{awards.map((award, index) => <span key={award + '-' + index}>{award}</span>)}</div>}{sourceDate && <small className="analysis-g2-freshness">Rilevato il {sourceDate}</small>}</section>}
    {meta && <SiteSignal pageId={meta.pageId} autoRunId={autoRunId} />}
    <div className="analysis-row-actions">{meta && <><Link to={'/competitor/' + encodeURIComponent(meta.pageId)} state={{ from: '/competitor', backLabel: 'Panorama' }}>Apri dettaglio</Link><Link to={'/annunci?pageId=' + encodeURIComponent(meta.pageId) + '&pageName=' + encodeURIComponent(item.name)} state={{ from: '/competitor', backLabel: 'Panorama' }}>Vedi annunci</Link></>}{productUrl && <a href={productUrl} target="_blank" rel="noreferrer">Scheda G2 ↗</a>}</div>
  </article>;
}

function UnifiedOnlineGroup({ title, description, rows, pages, markets, autoSiteIds, autoRunId }: { title: string; description: string; rows: UnifiedCompetitor[]; pages: PageView[]; markets: string[]; autoSiteIds?: Set<string>; autoRunId?: string }) {
  if (rows.length === 0) return null;
  const pageById = new Map(pages.map((page) => [page.pageId, page]));
  return <section className="analysis-result-group"><div className="section-heading"><div><h2>{title}</h2><p>{description}</p></div><span>{rows.length}</span></div><div className="analysis-competitor-list">{rows.map((item) => <UnifiedOnlineCompetitorRow key={item.key} item={item} page={item.meta ? pageById.get(item.meta.pageId) : undefined} markets={markets} autoRunId={item.meta && autoSiteIds?.has(item.meta.pageId) ? autoRunId : undefined} />)}</div></section>;
}

function SocialLanguage({ hashtags, tiktokUsed, warning }: { hashtags: HashtagSignal[]; tiktokUsed: boolean; warning: string | null }) {
  if (hashtags.length === 0 && !warning) return null;
  return (
    <section className="analysis-social-language" aria-labelledby="social-language-title">
      <div className="section-heading"><div><h2 id="social-language-title">Linguaggio social <span className="source-cost-badge">Apify</span></h2><p>Termini emersi da TikTok e Bluesky, usati come ricerche aggiuntive</p></div></div>
      {hashtags.length > 0 && <div className="analysis-hashtags">{hashtags.map((hashtag) => <span key={hashtag.tag}><strong>#{hashtag.tag}</strong><small>{numFmt.format(hashtag.count)} segnali · {hashtag.sources.map((source) => source === 'tiktok' ? 'TikTok' : source === 'bluesky' ? 'Bluesky' : source).join(' + ')}</small></span>)}</div>}
      {tiktokUsed && <p className="data-note">TikTok è stato interrogato tramite Apify. Il conteggio descrive occorrenze pubbliche, non il volume totale della piattaforma.</p>}
      {warning && <p className="analysis-social-warning">{warning}. L’analisi è proseguita con le altre fonti.</p>}
    </section>
  );
}

function LocalCompetitorRow({ item, index, average, maxReviews, isLeader, marketCount, radiusMeters, center, competitors }: { item: MarketCompetitor; index: number; average: number; maxReviews: number; isLeader: boolean; marketCount: number; radiusMeters: number | null; center: MarketMap['center']; competitors: MarketCompetitor[] }) {
  const opportunity = opportunityScore(item, average) > 0;
  const badge = isLeader ? 'Leader' : (item.rating ?? 0) >= average ? 'Competitivo' : 'Emergente';
  const detailUrl = localBusinessDetailUrl(item, { competitors: marketCount, radiusMeters, lat: center?.lat, lng: center?.lng });
  const subjectName = normalizedCompetitorName(item.name ?? '');
  const reportCompetitors = competitors.reduce<BusinessReportCompetitor[]>((rows, competitor) => {
    const competitorName = competitor.name?.trim();
    if (!competitorName || normalizedCompetitorName(competitorName) === subjectName) return rows;
    rows.push({ name: competitorName, rating: competitor.rating, reviewsCount: competitor.reviewsCount });
    return rows;
  }, []);
  const detailState = reportCompetitors.length > 0 ? { competitors: reportCompetitors } : undefined;
  return (
    <article className="analysis-local-row">
      <Link className="analysis-local-open" to={detailUrl} state={detailState} aria-label={`Apri il dettaglio di ${item.name ?? 'questa attività'}`} />
      <span className="analysis-rank">{index + 1}</span>
      <CategoryIcon category={item.primaryType ?? item.name} className="analysis-local-category"/>
      <div className="analysis-local-copy"><div className="analysis-local-name"><h3>{item.name ?? 'Attività senza nome'}</h3><span className={`market-rank-badge is-${badge.toLowerCase()}`}>{badge}</span></div>{(item.primaryType || item.distanceMeters != null) && <p className="analysis-local-meta">{[item.primaryType, item.distanceMeters != null ? 'a ' + formatDistance(item.distanceMeters) : null].filter(Boolean).join(' · ')}</p>}<p>{item.address ?? 'Indirizzo non disponibile'}</p></div>
      <dl><div><dt>Rating</dt><dd>{item.rating != null ? '★ ' + item.rating : 'n/d'}</dd></div><div><dt>Recensioni</dt><dd>{numFmt.format(item.reviewsCount ?? 0)}</dd></div></dl>
      <MetricBars value={item.reviewsCount ?? 0} max={maxReviews} label={`${item.reviewsCount ?? 0} recensioni`}/>
      <span className={opportunity ? 'presence-label opportunity' : item.strongPresence ? 'presence-label strong' : 'presence-label'}>{opportunity ? 'Spazio digitale' : item.strongPresence ? 'Presenza forte' : 'Da valutare'}</span>
      <div className="analysis-local-links">{item.website && <a href={item.website} target="_blank" rel="noreferrer">Sito</a>}{item.mapsUrl && <a href={item.mapsUrl} target="_blank" rel="noreferrer">Maps ↗</a>}<Link className="analysis-local-details" to={detailUrl} state={detailState}>Dettagli →</Link></div>
    </article>
  );
}

function LocalScope({ market }: { market: MarketMap }) {
  if (market.radiusMeters == null) return null;
  const radius = formatDistance(market.radiusMeters);
  return <section className="analysis-local-scope" aria-label="Contesto geografico dell’analisi">
    <div><span>Area analizzata</span><p>{market.population != null ? <><strong>~{numFmt.format(market.population)} abitanti</strong> nel raggio di {radius} <small className="access-label estimated">Stima</small></> : <><strong>Raggio di {radius}</strong> dal centro dell’indirizzo indicato</>}</p></div>
    <small>{market.population != null ? 'Popolazione: Censimento ISTAT 2021 (raggi ≥1,5 km) o stima WorldPop 2020. Le attività sono filtrate entro il raggio scelto.' : 'Le attività sono filtrate entro il raggio scelto. La stima degli abitanti non era disponibile.'}</small>
  </section>;
}

function LocalMarketGaps({ gaps }: { gaps: NonNullable<MarketMap['gaps']> }) {
  if (!gaps.reading && gaps.missing.length === 0 && gaps.oversupplied.length === 0) return null;
  return <section className="analysis-market-gaps" aria-labelledby="local-market-gaps-title">
    <div className="section-heading"><div><h2 id="local-market-gaps-title">Cosa manca, cosa è saturo</h2><p>Lettura del tessuto commerciale nel raggio selezionato</p></div></div>
    {gaps.reading && <p className="analysis-market-reading">{gaps.reading}</p>}
    <div className="analysis-market-gap-groups">
      {gaps.missing.length > 0 && <div><strong>Offerte assenti o deboli</strong><ul className="is-missing">{gaps.missing.map((item) => <li key={item}>{item}</li>)}</ul></div>}
      {gaps.oversupplied.length > 0 && <div><strong>Tipologie già molto presenti</strong><ul className="is-oversupplied">{gaps.oversupplied.map((item) => <li key={item}>{item}</li>)}</ul></div>}
    </div>
    <p className="data-note">Lettura AI basata su attività, reputazione e dati pubblici disponibili. È un’ipotesi da verificare, non una certezza di domanda.</p>
  </section>;
}

function ObservedCompetitors({ pages }: { pages: PageView[] }) {
  const [query, setQuery] = useState('');
  const rows = useMemo(() => pages
    .filter((page) => (page.pageName ?? page.pageId).toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => b.totalReach - a.totalReach), [pages, query]);
  if (pages.length === 0) return null;

  return (
    <details className="analysis-observed">
      <summary><span><strong>Tutti i competitor osservati</strong><small>Dati già raccolti, anche fuori da questa analisi</small></span><span>{pages.length}</span></summary>
      <div className="analysis-observed-body">
        <label className="field"><span>Cerca nei dati osservati</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nome azienda" /></label>
        <div>{rows.map((page) => <article className="observed-row" key={page.pageId}><div><strong>{page.pageName ?? page.pageId}</strong><small>{page.category ? categoryLabel(page.category) : 'Categoria non assegnata'} · {numFmt.format(page.adsCount)} annunci</small></div><span>{compactFmt.format(page.totalReach)} reach</span><Link to={'/competitor/' + encodeURIComponent(page.pageId)} state={{ from: '/competitor', backLabel: 'Panorama' }}>Dettaglio</Link></article>)}</div>
        {rows.length === 0 && <p className="muted">Nessun competitor corrisponde a “{query}”.</p>}
      </div>
    </details>
  );
}

export default function CompetitorAnalysis() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const requestParams = new URLSearchParams(location.search);
  const requestedAnalysisId = requestParams.get('analysis');
  const isDedicatedEntry = ['/competitor/scopri', '/competitor/mercato', '/competitor/locale'].includes(location.pathname);
  const forceNew = requestParams.get('new') === '1' || (isDedicatedEntry && !requestedAnalysisId);
  const routeIntent = location.pathname === '/competitor/mercato' ? 'opportunity' : 'competitors';
  const routeMode = location.pathname === '/competitor/scopri' ? 'online' : 'local';
  const requestedIntent = requestParams.has('intent')
    ? requestParams.get('intent') === 'opportunity' ? 'opportunity' : 'competitors'
    : routeIntent;
  const requestedMode = requestParams.has('mode')
    ? requestParams.get('mode') === 'online' ? 'online' : 'local'
    : routeMode;
  const requestedZone = requestParams.get('zone') ?? '';
  const cached = queryClient.getQueryData<AnalysisSession>(CACHE_KEY);
  const [session, setSession] = useState<AnalysisSession | null>(requestedAnalysisId || forceNew ? null : cached ?? null);
  const [restoredAnalysis, setRestoredAnalysis] = useState<SavedAnalysis<AnalysisSession> | null>(null);
  const [skipAutoRestore, setSkipAutoRestore] = useState(forceNew);
  const [analysisIntent, setAnalysisIntent] = useState<'competitors' | 'opportunity'>(requestedIntent);
  const [mode, setMode] = useState<AnalysisMode>(requestedMode);
  const [description, setDescription] = useState('');
  const [zone, setZone] = useState(requestedZone);
  const [radiusMeters, setRadiusMeters] = useState(cached?.market?.radiusMeters ?? 2_000);
  const [businessType, setBusinessType] = useState('');
  const [countries, setCountries] = useState<string[]>(['IT']);
  const [siteUrl, setSiteUrl] = useState(cached?.siteUrl ?? '');
  const [knownCompetitors, setKnownCompetitors] = useState<string[]>(cached?.knownCompetitors ?? []);
  const [knownCompetitorDraft, setKnownCompetitorDraft] = useState('');
  const [showOtherMarkets, setShowOtherMarkets] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [debouncedAddress, setDebouncedAddress] = useState('');
  const [addressPicked, setAddressPicked] = useState(false);
  const [addressMenuOpen, setAddressMenuOpen] = useState(false);
  const [activeAddressIndex, setActiveAddressIndex] = useState(-1);
  const [progress, setProgress] = useState<ProgressState>({ step: 1, detail: '' });
  const [opportunityProgress, setOpportunityProgress] = useState<ProgressState>({ step: 1, detail: '' });
  const [saasProgress, setSaasProgress] = useState<SaasProgress>('idle');
  const pages = useQuery({ queryKey: ['pages'], queryFn: fetchPages, staleTime: 60_000 });
  const savedAnalyses = useQuery({
    queryKey: ['saved-analyses', 0, 20],
    queryFn: () => fetchSavedAnalyses(0, 20),
    enabled: !session && !requestedAnalysisId && !skipAutoRestore,
    staleTime: 30_000,
  });
  const analysisToRestoreId = requestedAnalysisId
    ?? (!skipAutoRestore ? savedAnalyses.data?.content[0]?.id : undefined)
    ?? null;
  const analysisToRestore = useQuery({
    queryKey: ['saved-analysis', analysisToRestoreId],
    queryFn: () => fetchSavedAnalysis<AnalysisSession>(analysisToRestoreId!),
    enabled: !session && Boolean(analysisToRestoreId),
    staleTime: 5 * 60_000,
  });
  const addressSuggestions = useQuery({
    queryKey: ['market-address-suggestions', debouncedAddress],
    queryFn: ({ signal }) => suggestMarketAddresses(debouncedAddress, 6, signal),
    enabled: !session && mode === 'local' && debouncedAddress.length >= 3,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const persistAnalysis = useMutation({
    mutationFn: (next: AnalysisSession) => createSavedAnalysis({
      title: analysisTitle(next),
      mode: next.mode,
      description: next.description,
      summary: analysisSummary(next),
      payload: next,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['saved-analyses'] }),
  });

  useEffect(() => {
    const normalizedAddress = zone.trim();
    if (mode !== 'local' || addressPicked || normalizedAddress.length < 3) {
      setDebouncedAddress('');
      return;
    }
    const timer = window.setTimeout(() => setDebouncedAddress(normalizedAddress), 300);
    return () => window.clearTimeout(timer);
  }, [addressPicked, mode, zone]);

  useEffect(() => {
    setActiveAddressIndex(-1);
  }, [addressSuggestions.data]);

  useEffect(() => {
    if (session || !analysisToRestore.data) return;
    const restored = analysisToRestore.data;
    setSession(restored.payload);
    setRestoredAnalysis(restored);
    setMode(restored.payload.mode);
    setSelectedCountry(null);
    queryClient.setQueryData(CACHE_KEY, restored.payload);
  }, [analysisToRestore.data, queryClient, session]);

  useEffect(() => {
    if (!requestedAnalysisId || !restoredAnalysis || restoredAnalysis.id === requestedAnalysisId) return;
    setSession(null);
    setRestoredAnalysis(null);
    queryClient.removeQueries({ queryKey: CACHE_KEY, exact: true });
  }, [queryClient, requestedAnalysisId, restoredAnalysis]);

  const analysis = useMutation({
    mutationFn: async (): Promise<AnalysisSession> => {
      const analyzedCountries = mode === 'local' ? ['IT'] : uniqueTerms(countries).map((country) => country.toUpperCase());
      const seedSiteUrl = mode === 'online' ? siteUrl.trim() : '';
      const seedCompetitors = mode === 'online'
        ? uniqueTerms([...knownCompetitors, knownCompetitorDraft]).slice(0, 5)
        : [];
      const hasSeeds = Boolean(seedSiteUrl || seedCompetitors.length > 0);
      const base = { mode, description: description.trim(), zone: zone.trim(), businessType: businessType.trim(), countries: analyzedCountries, siteUrl: seedSiteUrl, knownCompetitors: seedCompetitors };
      if (mode === 'online') {
        setKnownCompetitors(seedCompetitors);
        setKnownCompetitorDraft('');
      }
      setProgress({ step: 1, detail: mode === 'local' ? 'Leggo servizio e area indicati' : hasSeeds ? 'Leggo il tuo sito e i competitor indicati' : 'Genero keyword nella lingua di ogni mercato' });

      if (mode === 'online') {
        setSaasProgress('running');
        let saasFinished = false;
        const saasPromise = discoverSaas(description.trim(), seedCompetitors)
          .then((result) => {
            saasFinished = true;
            setSaasProgress(result.error ? 'partial' : 'done');
            return result;
          })
          .catch((error) => {
            saasFinished = true;
            setSaasProgress('partial');
            return { categories: [], candidates: 0, competitors: [], error: errorMessage(error) } satisfies SaasDiscoveryResult;
          });
        const suggested = await discoverKeywords(description.trim(), analyzedCountries, {
          siteUrl: seedSiteUrl || undefined,
          knownCompetitors: seedCompetitors,
        });
        const keywordGroups = suggested.map((group) => ({
          ...group,
          countries: uniqueTerms(group.countries).map((country) => country.toUpperCase()),
          keywords: group.keywords.filter((keyword) => keyword.term.trim()),
        })).filter((group) => group.keywords.length > 0 && group.countries.length > 0);
        const baseKeywords = uniqueTerms(keywordGroups.flatMap((group) => group.keywords.map((keyword) => keyword.term)));
        if (baseKeywords.length === 0) throw new Error('Non sono riuscito a ricavare termini utili. Rendi la descrizione più specifica.');

        const primaryGroup = keywordGroups.find((group) => group.countries.includes(analyzedCountries[0])) ?? keywordGroups[0];
        const hashtagPromise = discoverHashtags(primaryGroup.keywords.map((keyword) => keyword.term), true)
          .catch(() => ({ hashtags: [], tiktokUsed: false, tiktokError: 'Il linguaggio social non era disponibile in questo momento' }));
        const candidates = new Map<string, number>();
        const knownCandidateIds = new Set<string>();
        const failedTerms: FailedSearch[] = [];
        const knownCompetitorsNotFound: string[] = [];
        const searched = new Set<string>();

        for (let index = 0; index < seedCompetitors.length; index += 1) {
          const seed = seedCompetitors[index];
          const term = competitorSearchTerm(seed);
          if (!term) continue;
          searched.add(term.toLocaleLowerCase('it'));
          setProgress({ step: 2, detail: 'Cerco il competitor indicato ' + (index + 1) + ' di ' + seedCompetitors.length + ': ' + seed });
          try {
            const outcome = await searchLive({ searchTerms: term, countries: analyzedCountries, limit: 50, origin: 'analisi' });
            const resultPages = outcome.pages ?? [];
            resultPages.forEach((page) => candidates.set(page.pageId, Math.max(candidates.get(page.pageId) ?? 0, page.totalReach)));
            const matchingPages = resultPages.filter((page) => competitorMatchesPage(seed, page.pageName));
            matchingPages.forEach((page) => knownCandidateIds.add(page.pageId));
            if (matchingPages.length === 0) knownCompetitorsNotFound.push(seed);
          } catch {
            failedTerms.push({ term: seed, language: 'semi', countries: analyzedCountries, source: 'seed' });
          }
        }

        for (const group of keywordGroups) {
          for (let index = 0; index < group.keywords.length; index += 1) {
            const term = group.keywords[index].term;
            const searchKey = term.toLocaleLowerCase('it');
            if (searched.has(searchKey)) continue;
            searched.add(searchKey);
            setProgress({ step: 2, detail: 'Cerco in ' + group.language + ': ' + (index + 1) + ' di ' + group.keywords.length + ' · social in ascolto' });
            try {
              const outcome = await searchLive({ searchTerms: term, countries: group.countries, limit: 50, origin: 'analisi' });
              (outcome.pages ?? []).forEach((page) => candidates.set(page.pageId, Math.max(candidates.get(page.pageId) ?? 0, page.totalReach)));
            } catch {
              failedTerms.push({ term, language: group.language, countries: group.countries, source: 'keyword' });
            }
          }
        }

        const hashtagResult = await hashtagPromise;
        const hashtags = hashtagResult.hashtags.slice(0, 5).map((hashtag) => ({ ...hashtag, tag: hashtag.tag.replace(/^#/, '') }));
        const socialTerms = uniqueTerms(hashtags.map((hashtag) => hashtag.tag));
        setProgress({ step: 3, detail: socialTerms.length > 0 ? socialTerms.length + ' termini dal linguaggio social da confrontare' : 'Completo il confronto nei mercati selezionati' });
        for (let index = 0; index < socialTerms.length; index += 1) {
          const term = socialTerms[index];
          setProgress({ step: 3, detail: 'Dal linguaggio social: ' + (index + 1) + ' di ' + socialTerms.length + ' · ' + term });
          try {
            const outcome = await searchLive({ searchTerms: term, countries: primaryGroup.countries, limit: 50, origin: 'analisi' });
            (outcome.pages ?? []).forEach((page) => candidates.set(page.pageId, Math.max(candidates.get(page.pageId) ?? 0, page.totalReach)));
          } catch {
            failedTerms.push({ term, language: primaryGroup.language, countries: primaryGroup.countries, source: 'social' });
          }
        }

        let classifications: CompetitorClassification[] = [];
        if (candidates.size > 0) {
          const rankedPageIds = [...candidates.entries()].sort((a, b) => b[1] - a[1]).map(([pageId]) => pageId);
          const pageIds = [...new Set([...knownCandidateIds, ...rankedPageIds])].slice(0, 30);
          setProgress({ step: 4, detail: 'Valuto ' + pageIds.length + ' aziende e rimuovo i risultati fuori mercato' });
          classifications = await discoverClassify(description.trim(), pageIds, seedCompetitors);
        } else {
          setProgress({ step: 4, detail: 'Non emergono inserzionisti Meta pertinenti; verifico il catalogo software' });
        }
        setProgress({ step: 5, detail: saasFinished ? 'Unisco i segnali Meta e G2 senza mescolare le metriche' : 'I dati Meta sono pronti; attendo il catalogo software G2' });
        const saas = await saasPromise;
        if (classifications.length === 0 && saas.competitors.length === 0) {
          const missingSeeds = knownCompetitorsNotFound.length > 0
            ? ' ' + knownCompetitorsNotFound.map((seed) => `${seed}: nessuna pubblicità Meta trovata.`).join(' ')
            : '';
          const g2Detail = saas.error ? ' Catalogo G2: ' + saas.error + '.' : '';
          throw new Error('Non sono emersi competitor pertinenti dalle fonti consultate.' + missingSeeds + g2Detail + ' Prova una descrizione più specifica.');
        }
        return {
          ...base,
          keywords: uniqueTerms([...baseKeywords, ...socialTerms, ...seedCompetitors.map(competitorSearchTerm)]),
          keywordGroups,
          hashtags,
          tiktokUsed: hashtagResult.tiktokUsed,
          socialWarning: hashtagResult.tiktokError,
          failedTerms,
          knownCompetitorsNotFound,
          classifications,
          saas,
          market: null,
          localAdsUnavailable: false,
          completedAt: new Date().toISOString(),
        };
      }

      const localQuery = businessType.trim() || description.trim();
      const localAddress = zone.trim();
      const useRadius = localAddress.length > 0;
      setSaasProgress('idle');
      const metaTerm = uniqueTerms([localQuery, localAddress]).join(' ');
      setProgress({ step: 2, detail: useRadius ? 'Risolvo l’indirizzo e filtro entro ' + formatDistance(radiusMeters) : 'Cerco “' + localQuery + '” nella zona descritta' });
      const progressTimers = useRadius ? [
        window.setTimeout(() => setProgress({ step: 2, detail: 'Stimo gli abitanti nel raggio e verifico le attività trovate' }), 8_000),
        window.setTimeout(() => setProgress({ step: 2, detail: 'Leggo cosa manca e cosa è già molto presente nella zona' }), 28_000),
      ] : [];
      const [marketResult, adsResult] = await (async () => {
        try {
          return await Promise.allSettled([
            fetchMarketNearby(localQuery, localAddress, 25, useRadius ? { address: localAddress, radiusMeters } : {}),
            searchLive({ searchTerms: metaTerm, countries: ['IT'], limit: 50, origin: 'analisi' }),
          ]);
        } finally {
          progressTimers.forEach((timer) => window.clearTimeout(timer));
        }
      })();
      if (marketResult.status === 'rejected') throw marketResult.reason;

      let classifications: CompetitorClassification[] = [];
      let localAdsUnavailable = adsResult.status === 'rejected';
      if (adsResult.status === 'fulfilled' && (adsResult.value.pages?.length ?? 0) > 0) {
        const pageIds = (adsResult.value.pages ?? []).sort((a, b) => b.totalReach - a.totalReach).slice(0, 30).map((page) => page.pageId);
        setProgress({ step: 3, detail: 'Confronto ' + pageIds.length + ' aziende che si promuovono nella zona' });
        try {
          classifications = await discoverClassify(description.trim(), pageIds);
        } catch {
          localAdsUnavailable = true;
        }
      } else {
        setProgress({ step: 3, detail: 'La mappa locale è pronta; non emergono dati pubblicitari confrontabili' });
      }
      setProgress({ step: 4, detail: 'Evidenzio densità, reputazione e spazi scoperti' });
      return {
        ...base,
        keywords: [metaTerm],
        keywordGroups: [],
        hashtags: [],
        tiktokUsed: false,
        socialWarning: null,
        failedTerms: [],
        knownCompetitorsNotFound: [],
        classifications,
        saas: null,
        market: marketResult.value,
        localAdsUnavailable,
        completedAt: new Date().toISOString(),
      };
    },
    onSuccess: (next) => {
      setSession(next);
      setRestoredAnalysis(null);
      setSelectedCountry(null);
      queryClient.setQueryData(CACHE_KEY, next);
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      queryClient.invalidateQueries({ queryKey: ['local'] });
      queryClient.invalidateQueries({ queryKey: ['local-page-groups'] });
      persistAnalysis.mutate(next);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        document.getElementById('analysis-result-title')?.focus({ preventScroll: true });
      });
    },
  });

  const opportunityAnalysis = useMutation({
    mutationFn: async (): Promise<AnalysisSession> => {
      const address = zone.trim();
      if (!address) throw new Error('Inserisci un indirizzo o una zona da censire.');
      setAddressMenuOpen(false);
      setSaasProgress('idle');
      setOpportunityProgress({ step: 1, detail: `Cerco il centro di “${address}”` });
      const timers = [
        window.setTimeout(() => setOpportunityProgress({ step: 2, detail: 'Confronto Google Maps e Registro Imprese' }), 3_000),
        window.setTimeout(() => setOpportunityProgress({ step: 3, detail: 'Stimo gli abitanti e calcolo la densità per categoria' }), 45_000),
        window.setTimeout(() => setOpportunityProgress({ step: 4, detail: 'Cerco 49 format tra gli hub europei e Milano' }), 75_000),
        window.setTimeout(() => setOpportunityProgress({ step: 5, detail: 'Ordino i segnali e preparo una lettura prudente' }), 105_000),
      ];
      let result: MarketOpportunityResult;
      try {
        result = await fetchMarketOpportunities(address, radiusMeters);
      } finally {
        timers.forEach((timer) => window.clearTimeout(timer));
      }
      return {
        kind: 'opportunity',
        mode: 'local',
        description: address,
        zone: address,
        businessType: 'Opportunità di zona',
        countries: ['IT'],
        keywords: [],
        keywordGroups: [],
        hashtags: [],
        tiktokUsed: false,
        socialWarning: null,
        failedTerms: [],
        knownCompetitorsNotFound: [],
        classifications: [],
        saas: null,
        market: null,
        opportunity: result,
        localAdsUnavailable: false,
        completedAt: new Date().toISOString(),
      };
    },
    onSuccess: (next) => {
      setSession(next);
      setRestoredAnalysis(null);
      setSelectedCountry(null);
      queryClient.setQueryData(CACHE_KEY, next);
      persistAnalysis.mutate(next);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        document.getElementById('analysis-result-title')?.focus({ preventScroll: true });
      });
    },
  });

  const follow = useMutation({
    mutationFn: async () => {
      if (!session) throw new Error('Completa prima un’analisi.');
      const selectedPages = session.classifications
        .filter((row) => row.label !== 'irrilevante')
        .sort((a, b) => b.totalReach - a.totalReach)
        .slice(0, 10)
        .map((row) => row.pageId);
      return createWatchlist({
        name: (session.mode === 'local' ? 'Mercato locale: ' : 'Mercato: ') + (session.businessType || session.description).slice(0, 90),
        searchTerms: session.keywords[0] || session.businessType || session.description,
        countries: session.countries,
        limit: 50,
        pageIds: selectedPages.length > 0 ? selectedPages : undefined,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['watchlists'] }),
  });

  useEffect(() => {
    if (!session) return;
    const topDirect = session.classifications
      .filter((row) => row.label === 'diretto')
      .sort((a, b) => b.totalReach - a.totalReach)
      .slice(0, 5)
      .map((row) => row.pageId);

    topDirect.forEach((pageId) => {
      const stateKey = ['site-auto', session.completedAt, pageId] as const;
      if (!queryClient.getQueryData(stateKey)) {
        queryClient.setQueryData<AutoSiteViewState>(stateKey, { status: 'loading', snapshot: null, message: null });
      }
      void scheduleAutoSite(session.completedAt, pageId).then((result) => {
        queryClient.setQueryData<AutoSiteViewState>(stateKey, result);
        if (result.status === 'ok' && result.snapshot) {
          queryClient.setQueryData(['site', pageId], result.snapshot);
        }
        queryClient.invalidateQueries({ queryKey: ['pages'] });
      }).catch(() => {
        queryClient.setQueryData<AutoSiteViewState>(stateKey, { status: 'error', snapshot: null, message: null });
      });
    });
  }, [queryClient, session]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (analysisIntent === 'opportunity') {
      if (zone.trim()) opportunityAnalysis.mutate();
      return;
    }
    const minimumLength = mode === 'local' ? 4 : 12;
    if (description.trim().length >= minimumLength && (mode === 'local' || countries.length > 0)) analysis.mutate();
  }

  function selectAddress(suggestion: AddressSuggestion) {
    setZone(suggestion.label);
    setAddressPicked(true);
    setAddressMenuOpen(false);
    setActiveAddressIndex(-1);
  }

  function handleAddressKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const suggestions = addressSuggestions.data ?? [];
    const menuVisible = addressMenuOpen
      && !addressPicked
      && debouncedAddress === zone.trim()
      && zone.trim().length >= 3;
    if (!menuVisible) return;
    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setActiveAddressIndex((current) => Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setActiveAddressIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter' && activeAddressIndex >= 0 && suggestions[activeAddressIndex]) {
      event.preventDefault();
      selectAddress(suggestions[activeAddressIndex]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setAddressMenuOpen(false);
      setActiveAddressIndex(-1);
    }
  }

  function addKnownCompetitor() {
    const next = uniqueTerms([...knownCompetitors, knownCompetitorDraft]).slice(0, 5);
    setKnownCompetitors(next);
    setKnownCompetitorDraft('');
  }

  function handleKnownCompetitorKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' && event.key !== ',') return;
    event.preventDefault();
    if (knownCompetitorDraft.trim()) addKnownCompetitor();
  }

  function toggleMarket(code: string) {
    setCountries((current) => current.includes(code)
      ? current.length === 1 ? current : current.filter((country) => country !== code)
      : [...current, code]);
  }

  function reset() {
    const previousMode = session?.mode ?? 'local';
    const previousSiteUrl = session?.siteUrl ?? '';
    const previousKnownCompetitors = session?.knownCompetitors ?? [];
    const previousRadius = session?.opportunity?.radiusMeters ?? session?.market?.radiusMeters ?? 2_000;
    setSession(null);
    setRestoredAnalysis(null);
    setSkipAutoRestore(true);
    queryClient.removeQueries({ queryKey: CACHE_KEY, exact: true });
    setMode(previousMode);
    setAnalysisIntent('competitors');
    setDescription('');
    setZone('');
    setDebouncedAddress('');
    setAddressPicked(false);
    setAddressMenuOpen(false);
    setActiveAddressIndex(-1);
    setRadiusMeters(previousRadius);
    setBusinessType('');
    setCountries(['IT']);
    setSiteUrl(previousSiteUrl);
    setKnownCompetitors(previousKnownCompetitors);
    setKnownCompetitorDraft('');
    setShowOtherMarkets(false);
    setSelectedCountry(null);
    setProgress({ step: 1, detail: '' });
    setOpportunityProgress({ step: 1, detail: '' });
    setSaasProgress('idle');
    analysis.reset();
    opportunityAnalysis.reset();
    persistAnalysis.reset();
    follow.reset();
    if (location.search) navigate('/competitor', { replace: true });
    window.scrollTo({ top: 0 });
  }

  if (analysis.isPending) return <ProgressView mode={mode} progress={progress} hasSeeds={Boolean(siteUrl.trim() || knownCompetitors.length > 0 || knownCompetitorDraft.trim())} saasProgress={saasProgress} />;
  if (opportunityAnalysis.isPending) return <OpportunityProgressView progress={opportunityProgress} />;

  const findingLatestAnalysis = !requestedAnalysisId && !skipAutoRestore && savedAnalyses.isPending;
  const loadingSavedAnalysis = Boolean(analysisToRestoreId) && analysisToRestore.isPending;
  if (!session && (findingLatestAnalysis || loadingSavedAnalysis)) return <RestoreAnalysisView />;

  if (!session) {
    const marketOptions = showOtherMarkets ? [...MAIN_MARKETS, ...OTHER_MARKETS] : MAIN_MARKETS;
    const minimumDescriptionLength = mode === 'local' ? 4 : 12;
    const descriptionTooShort = description.trim().length < minimumDescriptionLength;
    const marketsMissing = analysisIntent === 'competitors' && mode === 'online' && countries.length === 0;
    const submitBlockReason = analysisIntent === 'opportunity'
      ? zone.trim() ? null : 'Inserisci un indirizzo o una zona da valutare.'
      : descriptionTooShort
        ? `Descrivi l’attività con qualche parola in più (minimo ${minimumDescriptionLength} caratteri).`
        : marketsMissing ? 'Seleziona almeno un mercato da confrontare.' : null;
    const showAddressSuggestions = mode === 'local'
      && addressMenuOpen
      && !addressPicked
      && zone.trim().length >= 3
      && debouncedAddress === zone.trim();
    const suggestedAddresses = addressSuggestions.data ?? [];
    return (
      <div className="analysis-start-page">
        <header className="analysis-start-copy"><div><p className="eyebrow">Analizza</p><h1>Cosa vuoi capire?</h1><p>Scegli una domanda. Mostrerò solo i dati necessari.</p></div><span className="analysis-start-mark" aria-hidden="true"><svg viewBox="0 0 120 120" fill="none"><circle cx="60" cy="60" r="48"/><circle cx="60" cy="60" r="34"/><circle cx="60" cy="60" r="20"/><path d="M38 60c6-10 13-15 22-15s16 5 22 15c-6 10-13 15-22 15S44 70 38 60Z"/><circle cx="60" cy="60" r="6"/><path d="M60 12v8m0 80v8M12 60h8m80 0h8"/></svg></span></header>
        {(savedAnalyses.isError || analysisToRestore.isError) && <div className="notice notice-warning"><p>Non riesco a riaprire le analisi salvate. Puoi comunque iniziarne una nuova.</p><button type="button" className="btn-secondary" onClick={() => analysisToRestoreId ? analysisToRestore.refetch() : savedAnalyses.refetch()}>Riprova</button></div>}
        <form className="analysis-start-form" onSubmit={submit}>
          <fieldset className="analysis-mode analysis-intent"><legend>Scegli il tipo di analisi</legend><label className={analysisIntent === 'competitors' ? 'is-selected' : undefined}><input type="radio" name="analysis-intent" value="competitors" checked={analysisIntent === 'competitors'} onChange={() => setAnalysisIntent('competitors')} /><AnalysisChoiceIcon name="competitors"/><strong>Trova competitor</strong><small>Chi presidia già il mercato</small></label><label className={analysisIntent === 'opportunity' ? 'is-selected' : undefined}><input type="radio" name="analysis-intent" value="opportunity" checked={analysisIntent === 'opportunity'} onChange={() => { setAnalysisIntent('opportunity'); setMode('local'); }} /><AnalysisChoiceIcon name="opportunity"/><strong>Valuta una zona</strong><small>Densità, gap e format emergenti</small></label></fieldset>

          {analysisIntent === 'competitors' && <>
            <label className="field"><span>Descrivi la tua attività o idea <small>Obbligatorio</small></span><textarea rows={4} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Es. pizzeria d’asporto oppure software per gestire le email dei team" /></label>
            <fieldset className="analysis-mode"><legend>Dove opera?</legend><label className={mode === 'local' ? 'is-selected' : undefined}><input type="radio" name="analysis-mode" value="local" checked={mode === 'local'} onChange={() => setMode('local')} /><AnalysisChoiceIcon name="local"/><strong>Attività locale</strong><small>Clienti legati a una zona</small></label><label className={mode === 'online' ? 'is-selected' : undefined}><input type="radio" name="analysis-mode" value="online" checked={mode === 'online'} onChange={() => setMode('online')} /><AnalysisChoiceIcon name="online"/><strong>Business online</strong><small>Uno o più mercati</small></label></fieldset>
          </>}

          {(analysisIntent === 'opportunity' || mode === 'local') && <details className={`analysis-optional analysis-zone-fields${analysisIntent === 'opportunity' ? ' is-required' : ''}`} open={analysisIntent === 'opportunity' ? true : undefined} onToggle={analysisIntent === 'opportunity' ? (event) => { event.currentTarget.open = true; } : undefined}>
            <summary><span><strong>Definisci una zona</strong><small>Indirizzo e raggio, facoltativi</small></span><span aria-hidden="true">+</span></summary>
            <div className="analysis-local-fields">
            <div className="field analysis-address-field">
              <label htmlFor="local-address">Indirizzo o zona {analysisIntent === 'competitors' && <small>Opzionale</small>}</label>
              <div className="analysis-address-control">
                <input id="local-address" type="text" value={zone} onChange={(event) => { setZone(event.target.value); setAddressPicked(false); setAddressMenuOpen(true); }} onFocus={() => { if (!addressPicked) setAddressMenuOpen(true); }} onBlur={() => { setAddressMenuOpen(false); setActiveAddressIndex(-1); }} onKeyDown={handleAddressKeyDown} placeholder="Es. via Casilina 1300, Roma" autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={showAddressSuggestions} aria-controls={showAddressSuggestions ? 'local-address-suggestions' : undefined} aria-activedescendant={showAddressSuggestions && activeAddressIndex >= 0 ? `local-address-option-${activeAddressIndex}` : undefined} aria-describedby="local-address-hint" />
                {showAddressSuggestions && <div className="analysis-address-menu">
                  {addressSuggestions.isFetching ? <p id="local-address-suggestions" className="analysis-address-status" role="status">Cerco indirizzi…</p>
                    : addressSuggestions.isError ? <p id="local-address-suggestions" className="analysis-address-status">Suggerimenti non disponibili. Puoi scrivere l’indirizzo.</p>
                      : suggestedAddresses.length === 0 ? <p id="local-address-suggestions" className="analysis-address-status">Nessun indirizzo trovato.</p>
                        : <ul id="local-address-suggestions" role="listbox" aria-label="Indirizzi suggeriti">{suggestedAddresses.map((suggestion, index) => <li role="none" key={`${suggestion.label}-${index}`}><button id={`local-address-option-${index}`} type="button" role="option" tabIndex={-1} aria-selected={activeAddressIndex === index} className={activeAddressIndex === index ? 'is-active' : undefined} onPointerDown={(event) => { event.preventDefault(); selectAddress(suggestion); }} onClick={() => selectAddress(suggestion)} onMouseEnter={() => setActiveAddressIndex(index)}><strong>{suggestion.main || suggestion.label}</strong>{suggestion.secondary && <small>{suggestion.secondary}</small>}</button></li>)}</ul>}
                </div>}
              </div>
              <small id="local-address-hint">Digita almeno 3 caratteri. Puoi scegliere un suggerimento o inserire la zona manualmente.</small>
            </div>
            <fieldset className={`analysis-radius analysis-radius-slider${zone.trim() ? '' : ' is-disabled'}`} disabled={!zone.trim()}><legend><span>Raggio di analisi</span><strong>{formatDistance(radiusMeters)}</strong></legend><label><span className="sr-only">Scegli il raggio di analisi</span><input type="range" min="0" max={LOCAL_RADII.length - 1} step="1" value={Math.max(0, LOCAL_RADII.findIndex((option) => option.value === radiusMeters))} onChange={(event) => setRadiusMeters(LOCAL_RADII[Number(event.target.value)]?.value ?? 2_000)} /></label><div className="analysis-radius-stops" aria-hidden="true">{LOCAL_RADII.map((option) => <span className={radiusMeters === option.value ? 'is-selected' : undefined} key={option.value}>{option.label}</span>)}</div><p>{zone.trim() ? 'Include solo le attività entro questa distanza dal centro risolto.' : 'Inserisci prima una zona per scegliere il raggio.'}</p></fieldset>
            </div>
          </details>}
          {analysisIntent === 'competitors' && mode === 'online' && <>
            <fieldset className="analysis-markets" aria-describedby="market-selection-note"><legend>Mercati da confrontare <small>{countries.length} selezionati</small></legend><p id="market-selection-note">Le keyword saranno generate nella lingua usata dagli inserzionisti di ogni paese.</p><div className="analysis-market-chips">{marketOptions.map((market) => <label className={countries.includes(market.code) ? 'is-selected' : undefined} key={market.code}><input type="checkbox" checked={countries.includes(market.code)} onChange={() => toggleMarket(market.code)} /><strong>{market.code}</strong><span>{market.label}</span></label>)}<button type="button" className="analysis-more-markets" aria-expanded={showOtherMarkets} onClick={() => setShowOtherMarkets((current) => !current)}>{showOtherMarkets ? 'Meno mercati' : 'Altri mercati'}</button></div><p className="analysis-apify-note"><span className="source-cost-badge">Apify</span> L’analisi online interroga TikTok e il catalogo software G2. Può richiedere 1–3 minuti e consuma credito.</p></fieldset>
            <details className="analysis-optional"><summary><span><strong>Dati che già conosci</strong><small>Sito e competitor noti, facoltativi</small></span><span aria-hidden="true">+</span></summary><AnalysisSeeds siteUrl={siteUrl} knownCompetitors={knownCompetitors} draft={knownCompetitorDraft} onSiteUrlChange={setSiteUrl} onDraftChange={setKnownCompetitorDraft} onDraftKeyDown={handleKnownCompetitorKeyDown} onAdd={addKnownCompetitor} onRemove={(value) => setKnownCompetitors((current) => current.filter((competitor) => competitor !== value))} /></details>
          </>}
          <div className="analysis-submit-actions is-single"><button type="submit" className="btn-primary analysis-submit" disabled={Boolean(submitBlockReason)} aria-describedby={submitBlockReason ? 'analysis-submit-reason' : analysisIntent === 'opportunity' ? 'analysis-opportunity-hint' : undefined}>{analysisIntent === 'opportunity' ? 'Valuta la zona' : 'Analizza il mercato'}</button></div>
          {submitBlockReason && <p id="analysis-submit-reason" className="analysis-submit-reason" role="status">{submitBlockReason}</p>}
          {analysisIntent === 'opportunity' && zone.trim() && <p id="analysis-opportunity-hint" className="analysis-opportunity-hint">Censisce tutte le categorie entro {formatDistance(radiusMeters)}. Può richiedere fino a 2 minuti.</p>}
          <Link className="btn-quiet analysis-history-link" to="/monitoraggi">Analisi precedenti{savedAnalyses.data?.totalElements ? ` (${savedAnalyses.data.totalElements})` : ''}</Link>
          {pages.isSuccess && pages.data.length > 0 && <p className="analysis-existing-note">Userò anche i dati già osservati su {pages.data.length} competitor.</p>}
          {analysis.isError && <div className="notice notice-error" role="alert"><p>{errorMessage(analysis.error)}</p><button type="button" className="btn-secondary" onClick={() => analysis.mutate()}>Riprova</button></div>}
          {opportunityAnalysis.isError && <div className="notice notice-error" role="alert"><p>{errorMessage(opportunityAnalysis.error)}</p><button type="button" className="btn-secondary" onClick={() => opportunityAnalysis.mutate()}>Riprova</button></div>}
        </form>
      </div>
    );
  }

  if (session.opportunity) return <OpportunityResultsView session={session} restoredAnalysis={restoredAnalysis} saveState={persistAnalysis.status} onReset={reset} onRetrySave={() => persistAnalysis.mutate(session)} />;

  const analyzedMarkets = session.countries?.length ? session.countries : ['IT'];
  const activeCountry = selectedCountry && analyzedMarkets.includes(selectedCountry) ? selectedCountry : null;
  const direct = session.classifications.filter((row) => row.label === 'diretto');
  const autoSiteIds = new Set([...direct].sort((a, b) => b.totalReach - a.totalReach).slice(0, 5).map((row) => row.pageId));
  const adjacent = session.classifications.filter((row) => row.label === 'adiacente');
  const irrelevant = session.classifications.filter((row) => row.label === 'irrilevante');
  const unified = mergeCompetitors(session.classifications, session.saas?.competitors ?? []);
  const unifiedDirect = unified.filter((row) => row.label === 'diretto');
  const unifiedAdjacent = unified.filter((row) => row.label === 'adiacente');
  const unifiedRelevant = unified.filter((row) => row.label !== 'irrilevante');
  const unifiedIrrelevant = unified.filter((row) => row.label === 'irrilevante');
  const leader = direct.reduce<CompetitorClassification | null>((current, row) => !current || row.totalReach > current.totalReach ? row : current, null);
  const relevant = rankByMarketStrength([...direct, ...adjacent], analyzedMarkets);
  const visibleIrrelevant = activeCountry ? irrelevant.filter((row) => rowCountries(row, analyzedMarkets).includes(activeCountry)) : irrelevant;
  const visibleUnifiedRelevant = activeCountry ? unifiedRelevant.filter((row) => Boolean(row.saas) || Boolean(row.meta && rowCountries(row.meta, analyzedMarkets).includes(activeCountry))) : unifiedRelevant;
  const visibleUnifiedIrrelevant = activeCountry ? unifiedIrrelevant.filter((row) => Boolean(row.saas) || Boolean(row.meta && rowCountries(row.meta, analyzedMarkets).includes(activeCountry))) : unifiedIrrelevant;
  const countrySummaries = analyzedMarkets.map((country) => {
    const countryDirect = direct.filter((row) => rowCountries(row, analyzedMarkets).includes(country));
    const countryRelevant = relevant.filter((row) => rowCountries(row, analyzedMarkets).includes(country));
    const countryLeader = (countryDirect.length > 0 ? countryDirect : countryRelevant).reduce<CompetitorClassification | null>((current, row) => !current || row.totalReach > current.totalReach ? row : current, null);
    return { country, directCount: countryDirect.length, relevantCount: countryRelevant.length, leader: countryLeader };
  });
  const marketRows = session.market ? [...session.market.competitors].sort((a, b) => opportunityScore(b, session.market!.avgRating) - opportunityScore(a, session.market!.avgRating) || (b.rating ?? 0) - (a.rating ?? 0)) : [];
  const maxMarketReviews = Math.max(1, ...marketRows.map((item) => item.reviewsCount ?? 0));
  const marketLeaderName = session.market?.leader ?? marketRows.reduce<MarketCompetitor | null>((leader, item) => !leader || (item.reviewsCount ?? 0) > (leader.reviewsCount ?? 0) ? item : leader, null)?.name;
  const localOpportunities = session.market?.competitors.filter((item) => opportunityScore(item, session.market!.avgRating) > 0) ?? [];
  const directMedian = direct.length > 0 ? [...direct].sort((a, b) => a.totalReach - b.totalReach)[Math.floor(direct.length / 2)].totalReach : 0;
  const lowerPressure = direct.filter((row) => row.totalReach < directMedian || (pages.data?.find((page) => page.pageId === row.pageId)?.activeCount ?? 1) === 0);
  const g2DirectCount = unifiedDirect.filter((row) => row.saas).length;
  const g2OnlyDirectCount = unifiedDirect.filter((row) => row.saas && !row.meta).length;
  const hasG2Results = (session.saas?.competitors.length ?? 0) > 0;
  const verdict = session.mode === 'local' && session.market
    ? session.market.count + (session.market.radiusMeters != null ? ' attività entro ' + formatDistance(session.market.radiusMeters) : ' attività nella zona') + ', rating medio ' + (session.market.avgRating || 'non disponibile') + '. ' + (localOpportunities.length > 0 ? localOpportunities.length + ' combinano buona reputazione e presenza digitale debole.' : 'Non emerge ancora uno spazio digitale netto.')
    : unifiedDirect.length + ' ' + (unifiedDirect.length === 1 ? 'competitor diretto emerge' : 'competitor diretti emergono') + (hasG2Results ? ' da Meta Ads e G2.' : ' dai dati Meta Ads.') + (leader ? ' Sul fronte pubblicitario guida ' + (leader.pageName ?? leader.pageId) + ' con circa ' + compactFmt.format(leader.totalReach) + ' di reach.' : '') + (g2DirectCount > 0 ? ' ' + g2DirectCount + ' ' + (g2DirectCount === 1 ? 'diretto ha' : 'diretti hanno') + ' segnali nel catalogo G2.' : '') + ' ' + unifiedAdjacent.length + ' ' + (unifiedAdjacent.length === 1 ? 'adiacente da osservare.' : 'adiacenti da osservare.');

  return (
    <div className="analysis-panorama">
      {restoredAnalysis && <div className="analysis-restored-note"><span>Analisi del <time dateTime={restoredAnalysis.createdAt}>{analysisDateFmt.format(new Date(restoredAnalysis.createdAt))}</time></span><button type="button" className="btn-quiet" onClick={reset}>Nuova analisi</button></div>}
      <header className="analysis-panorama-head"><div><p className="eyebrow">Panorama competitivo</p><h1 id="analysis-result-title" tabIndex={-1}>{session.businessType || session.description}</h1><p>{session.mode === 'local' ? [session.zone, 'attività locale'].filter(Boolean).join(' · ') : analyzedMarkets.map(marketName).join(' · ')}</p></div><div>{!restoredAnalysis && <button type="button" className="btn-secondary" onClick={reset}>Nuova analisi</button>}<button type="button" className="btn-primary" disabled={follow.isPending || follow.isSuccess} onClick={() => follow.mutate()}>{follow.isPending ? 'Salvo…' : follow.isSuccess ? 'Mercato seguito' : 'Segui questo mercato'}</button></div></header>

      {session.mode === 'local' && session.market?.radiusMeters != null && <section className="analysis-map-lead" aria-label="Area e raggio dell’analisi"><ZoneVisual radiusLabel={formatDistance(session.market.radiusMeters)} title={session.zone || session.market.area || 'Area analizzata'} markers={Math.min(7, session.market.competitors.length)}/></section>}

      <section className="analysis-verdict" aria-labelledby="analysis-verdict-title"><p className="eyebrow">Verdetto aggregato</p><h2 id="analysis-verdict-title">{verdict}</h2><div><span>{session.mode === 'local' ? 'Google Places + dati pubblicitari pubblici' : `Meta Ads + catalogo G2 + classificazione AI${session.siteUrl || session.knownCompetitors?.length ? ' + informazioni indicate' : ''}`}</span><span>{restoredAnalysis ? `Salvata il ${analysisDateFmt.format(new Date(restoredAnalysis.createdAt))}` : persistAnalysis.isPending ? 'Salvataggio automatico…' : persistAnalysis.isSuccess ? 'Salvata automaticamente' : persistAnalysis.isError ? 'Salvataggio non riuscito' : 'Aggiornato ora'}</span></div></section>
      {persistAnalysis.isError && <div className="notice notice-warning"><p>Il panorama è pronto, ma non è stato salvato nello storico.</p><button type="button" className="btn-secondary" onClick={() => persistAnalysis.mutate(session)}>Riprova il salvataggio</button></div>}
      {session.mode === 'local' && session.market && <LocalScope market={session.market} />}
      {follow.isSuccess && <p className="success-message">Monitoraggio creato. <Link to="/monitoraggi">Gestiscilo da Attività</Link>.</p>}
      {follow.isError && <p className="error">{errorMessage(follow.error)}</p>}
      {session.failedTerms.length > 0 && <p className="notice notice-warning">Analisi completata con {session.failedTerms.length} ricerche non concluse. {failedSearchSummary(session.failedTerms)}</p>}
      {(session.knownCompetitorsNotFound?.length ?? 0) > 0 && <section className="analysis-seed-coverage" aria-labelledby="seed-coverage-title"><div><strong id="seed-coverage-title">Competitor indicati non trovati su Meta</strong><p>Non risultano pagine pubblicitarie corrispondenti nei mercati selezionati.</p></div><ul>{session.knownCompetitorsNotFound!.map((competitor) => <li key={competitor}><span>{competitor}</span><small>Nessuna pubblicità Meta trovata</small></li>)}</ul></section>}

      {session.mode === 'online' && <>
        <section className="analysis-country-overview" aria-labelledby="country-overview-title"><div className="section-heading"><div><h2 id="country-overview-title">Mercati analizzati</h2><p>Il filtro paese riguarda Meta Ads; i competitor del catalogo G2 restano globali.</p></div></div><div className="analysis-country-strip" role="group" aria-label="Filtra competitor per paese">{countrySummaries.map((summary) => <button type="button" key={summary.country} className={activeCountry === summary.country ? 'is-active' : undefined} aria-pressed={activeCountry === summary.country} onClick={() => setSelectedCountry(activeCountry === summary.country ? null : summary.country)}><strong>{summary.country}</strong><span>{summary.directCount} {summary.directCount === 1 ? 'diretto Meta' : 'diretti Meta'}</span><small>{summary.leader ? 'Guida ' + (summary.leader.pageName ?? summary.leader.pageId) : summary.relevantCount > 0 ? summary.relevantCount + ' rilevanti' : 'Nessun risultato'}</small></button>)}</div></section>
        <SocialLanguage hashtags={session.hashtags ?? []} tiktokUsed={session.tiktokUsed} warning={session.socialWarning} />
        {session.saas && <G2Coverage result={session.saas} />}
        <UnifiedOnlineGroup title={activeCountry ? 'Competitor per ' + marketName(activeCountry) + ' e catalogo G2' : 'Competitor rilevanti'} description={activeCountry ? 'Meta filtrato sul mercato ' + activeCountry + '; G2 resta una fonte globale' : 'Una sola lista: prima i risultati presenti in entrambe le fonti, poi Meta per reach e G2 per recensioni'} rows={visibleUnifiedRelevant} pages={pages.data ?? []} markets={analyzedMarkets} autoSiteIds={autoSiteIds} autoRunId={session.completedAt} />
        {visibleUnifiedRelevant.length === 0 && <section className="empty empty-compact"><h2>Nessun concorrente pertinente</h2><p>{activeCountry ? 'Non emergono competitor classificati in questo paese o nel catalogo G2. Prova un altro mercato o torna alla vista aggregata.' : 'Avvia una nuova analisi con una descrizione più specifica.'}</p></section>}
        {session.saas?.error && <p className="notice notice-warning">Catalogo G2: {session.saas.error}. {session.saas.competitors.length > 0 ? 'I risultati disponibili sono comunque inclusi nella lista.' : 'L’analisi Meta è stata completata normalmente.'}</p>}
      </>}

      {session.mode === 'local' && session.market && <>
        <section className="analysis-result-group"><div className="section-heading"><div><h2>Competitor nella zona</h2><p>{session.market.radiusMeters != null ? 'Filtrati entro ' + formatDistance(session.market.radiusMeters) + '; ordinati per opportunità digitale, rating e recensioni' : 'Ordinati per opportunità digitale, rating e recensioni'}</p></div><span>{marketRows.length}</span></div><div className="analysis-local-list">{marketRows.map((item, index) => <LocalCompetitorRow key={(item.name ?? 'attivita') + '-' + index} item={item} index={index} average={session.market!.avgRating} maxReviews={maxMarketReviews} isLeader={Boolean(item.name && item.name === marketLeaderName)} marketCount={session.market!.count} radiusMeters={session.market!.radiusMeters} center={session.market!.center} competitors={marketRows} />)}</div><p className="data-note">“Spazio digitale” è un’indicazione basata su reputazione, sito disponibile e volume di recensioni. Non misura vendite o spesa pubblicitaria.</p></section>
        {session.market.gaps && <LocalMarketGaps gaps={session.market.gaps} />}
        {direct.length + adjacent.length > 0 && <OnlineGroup title="Chi si promuove nella zona" description="Aziende pertinenti trovate nei dati pubblicitari pubblici" rows={[...direct, ...adjacent]} pages={pages.data ?? []} markets={['IT']} autoSiteIds={autoSiteIds} autoRunId={session.completedAt} />}
        {session.localAdsUnavailable && <p className="muted">La mappa locale è completa; i segnali pubblicitari non erano disponibili durante questa analisi.</p>}
      </>}

      <section className="analysis-gaps" aria-labelledby="analysis-gaps-title"><div className="section-heading"><div><h2 id="analysis-gaps-title">{session.mode === 'local' ? 'Segnali digitali' : 'Dove c’è spazio'}</h2><p>{session.mode === 'online' ? 'Lettura aggregata su tutti i mercati; segnali da verificare, non promesse di domanda' : 'Presenza online e prova sociale delle attività nel raggio'}</p></div></div><div>
        {session.mode === 'local' ? <>{localOpportunities.length > 0 ? <div><strong>{localOpportunities.length} attività con un gap digitale</strong><p>Hanno rating almeno in linea con la media ma una presenza digitale più debole: sono lo spazio più concreto da studiare.</p></div> : <div><strong>Mercato locale già presidiato</strong><p>Non emerge un vantaggio evidente dalla sola reputazione. Confronta servizi, prezzi e recensioni negative prima di decidere.</p></div>}{session.market?.leader && <div><strong>{session.market.leader} guida per volume</strong><p>Il leader ha la maggiore prova sociale nella zona; differenziarsi solo sul messaggio potrebbe non bastare.</p></div>}</> : <>{g2OnlyDirectCount > 0 && <div><strong>{g2OnlyDirectCount} {g2OnlyDirectCount === 1 ? 'diretto emerge' : 'diretti emergono'} solo dal catalogo G2</strong><p>Non compaiono tra gli inserzionisti Meta osservati: assenza di pubblicità non significa assenza dal mercato.</p></div>}{lowerPressure.length > 0 && <div><strong>{lowerPressure.length} diretti Meta sotto la pressione mediana</strong><p>La loro reach è più bassa o non hanno annunci attivi: verifica se presidiano segmenti lasciati scoperti.</p></div>}{unifiedAdjacent.length > 0 && <div><strong>{unifiedAdjacent.length} offerte adiacenti</strong><p>Mostrano bisogni vicini che i competitor diretti potrebbero non coprire completamente.</p></div>}{leader && direct.reduce((sum, row) => sum + row.totalReach, 0) > 0 && leader.totalReach / direct.reduce((sum, row) => sum + row.totalReach, 0) >= .5 && <div><strong>Mercato pubblicitario concentrato</strong><p>Il leader raccoglie almeno metà della reach osservata: cerca una posizione distinta invece di copiarne il messaggio.</p></div>}</>}
      </div></section>

      {session.mode === 'online' && visibleUnifiedIrrelevant.length > 0 && <details className="analysis-excluded"><summary><span><strong>Risultati esclusi</strong><small>Aziende e software fuori dal mercato descritto{activeCountry ? ' per ' + activeCountry : ''}</small></span><span>{visibleUnifiedIrrelevant.length}</span></summary><UnifiedOnlineGroup title="Esclusi dalla lettura" description="Disponibili per controllare la classificazione" rows={visibleUnifiedIrrelevant} pages={pages.data ?? []} markets={analyzedMarkets} /></details>}
      {session.mode === 'local' && visibleIrrelevant.length > 0 && <details className="analysis-excluded"><summary><span><strong>Risultati esclusi</strong><small>Aziende fuori dal mercato descritto</small></span><span>{visibleIrrelevant.length}</span></summary><OnlineGroup title="Esclusi dalla lettura" description="Disponibili per controllare la classificazione" rows={visibleIrrelevant} pages={pages.data ?? []} markets={analyzedMarkets} /></details>}

      {pages.isSuccess && <ObservedCompetitors pages={pages.data} />}
      {pages.isError && <p className="error">I competitor già osservati non sono disponibili: {errorMessage(pages.error)}</p>}

      <footer className="analysis-next"><div><strong>Vuoi importare altre aziende?</strong><p>Cerca su Meta con una keyword e un mercato specifici.</p></div><Link className="btn-secondary" to="/ricerca" state={{ from: location.pathname + location.search, backLabel: 'Panorama' }}>Cerca su Meta</Link></footer>
    </div>
  );
}
